/**
 * Business formation interview.
 * Question order follows the public formation flows (LegalZoom, ZenBusiness,
 * Bizee, Northwest): entity, where it is created, state, name, owners,
 * registered agent, workers, tax, activity, licenses, other states, bank.
 * Answers are stored on the console. Steps are derived, not a second checklist.
 */

import { emptyDodTaxonomy, newDodId } from './dodCmmcCatalog';
import {
  type BusinessCreationAnswers,
  type ComplianceChecklistItemDef,
  type ComplianceConsoleState,
  type ComplianceItemPriority,
  type CompliancePillarDef,
  type ComplianceVerifyStepDef,
  selectDodCompany,
} from './complianceChecklists';
import type { PricingTierId } from './pricingTiers';

export type FormationLanguage = 'en' | 'es';

type Pair = readonly [string, string];

const tx = (language: FormationLanguage, en: string, es: string): string => (
  language === 'es' ? es : en
);

const pair = (language: FormationLanguage, value: Pair): string => tx(language, value[0], value[1]);

export const US_FORMATION_STATES: ReadonlyArray<readonly [string, string]> = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
  ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

export const US_FORMATION_TERRITORIES: ReadonlyArray<readonly [string, string]> = [
  ['AS', 'American Samoa'],
  ['GU', 'Guam'],
  ['MP', 'Northern Mariana Islands'],
  ['PR', 'Puerto Rico'],
  ['VI', 'U.S. Virgin Islands'],
];

/** ISO region codes the formation picker can name. Filing rules stay with that place. */
export const FORMATION_COUNTRY_CODES: readonly string[] = [
  'AF','AL','DZ','AD','AO','AG','AR','AM','AU','AT','AZ','BS','BH','BD','BB','BY','BE','BZ','BJ','BT',
  'BO','BA','BW','BR','BN','BG','BF','BI','CV','KH','CM','CA','CF','TD','CL','CN','CO','KM','CG','CR',
  'CI','HR','CU','CY','CZ','DK','DJ','DM','DO','EC','EG','SV','GQ','ER','EE','SZ','ET','FJ','FI','FR',
  'GA','GM','GE','DE','GH','GR','GD','GT','GN','GW','GY','HT','HN','HU','IS','IN','ID','IR','IQ','IE',
  'IL','IT','JM','JP','JO','KZ','KE','KI','KW','KG','LA','LV','LB','LS','LR','LY','LI','LT','LU','MG',
  'MW','MY','MV','ML','MT','MH','MR','MU','MX','FM','MD','MC','MN','ME','MA','MZ','MM','NA','NR','NP',
  'NL','NZ','NI','NE','NG','KP','MK','NO','OM','PK','PW','PA','PG','PY','PE','PH','PL','PT','QA','RO',
  'RU','RW','KN','LC','VC','WS','SM','ST','SA','SN','RS','SC','SL','SG','SK','SI','SB','SO','ZA','KR',
  'SS','ES','LK','SD','SR','SE','CH','SY','TW','TJ','TZ','TH','TL','TG','TO','TT','TN','TR','TM','TV',
  'UG','UA','AE','GB','US','UY','UZ','VU','VE','VN','YE','ZM','ZW',
];

export const SANCTIONS_REVIEW_COUNTRIES = new Set(['BY', 'CU', 'IR', 'KP', 'RU', 'SY', 'VE']);

const STATE_NAME = new Map([
  ...US_FORMATION_STATES,
  ...US_FORMATION_TERRITORIES,
].map(([code, name]) => [code, name]));

export const formationCountryName = (code: string | undefined, language: FormationLanguage = 'en'): string => {
  const normalized = String(code || '').trim().toUpperCase();
  if (!normalized) {
    return '';
  }
  try {
    const label = new Intl.DisplayNames([language === 'es' ? 'es' : 'en'], { type: 'region' }).of(normalized);
    return label || normalized;
  } catch {
    return normalized;
  }
};

export const formationStateName = (code: string | undefined): string => (
  STATE_NAME.get(String(code || '').toUpperCase()) || String(code || '').toUpperCase()
);

const filled = (value: string | undefined): boolean => Boolean(String(value || '').trim());

const QUESTION_ORDER = [
  'entity',
  'origin',
  'homeCountry',
  'stage',
  'state',
  'legalName',
  'useDba',
  'dbaName',
  'owners',
  'management',
  'agent',
  'workers',
  'tax',
  'exempt',
  'activity',
  'offers',
  'salesTax',
  'extraStates',
  'extraStateList',
  'bank',
] as const;

export type BusinessQuestionId = typeof QUESTION_ORDER[number];

export type BusinessQuestionOption = {
  value: string;
  label: string;
  detail: string;
};

export type BusinessQuestion = {
  id: BusinessQuestionId;
  kind: 'choice' | 'text' | 'state' | 'country';
  prompt: string;
  help: string;
  options?: BusinessQuestionOption[];
  placeholder?: string;
};

const stateFiled = (answers: BusinessCreationAnswers): boolean => (
  Boolean(answers.entity) && answers.entity !== 'sole-prop'
);

export const questionApplies = (
  id: BusinessQuestionId,
  answers: BusinessCreationAnswers,
): boolean => {
  switch (id) {
    case 'entity':
      return true;
    case 'origin':
      return stateFiled(answers);
    case 'homeCountry':
      return answers.origin === 'foreign';
    case 'stage':
      if (!answers.entity) {
        return false;
      }
      if (answers.entity === 'sole-prop') {
        return true;
      }
      if (answers.origin === 'foreign') {
        return Boolean(answers.homeCountry && FORMATION_COUNTRY_CODES.includes(answers.homeCountry));
      }
      return answers.origin === 'us' || answers.origin === 'territory';
    case 'state':
      return Boolean(answers.stage) && answers.origin !== 'foreign';
    case 'legalName':
      return Boolean(
        (answers.state && STATE_NAME.has(answers.state))
        || (answers.homeCountry && FORMATION_COUNTRY_CODES.includes(answers.homeCountry)),
      );
    case 'useDba':
      return filled(answers.legalName);
    case 'dbaName':
      return answers.useDba === 'yes';
    case 'owners':
      return (answers.useDba === 'no' || filled(answers.dbaName))
        && (answers.entity === 'llc' || answers.entity === 'corporation' || answers.entity === 'partnership');
    case 'management':
      return answers.entity === 'llc' && Boolean(answers.owners);
    case 'agent':
      return stateFiled(answers) && (
        (answers.entity === 'llc' && Boolean(answers.management))
        || (answers.entity === 'corporation' && Boolean(answers.owners))
        || (answers.entity === 'partnership' && Boolean(answers.owners))
        || (answers.entity === 'nonprofit' && (answers.useDba === 'no' || filled(answers.dbaName)))
      );
    case 'workers':
      return answers.entity === 'sole-prop'
        ? (answers.useDba === 'no' || filled(answers.dbaName))
        : Boolean(answers.agent);
    case 'tax':
      return Boolean(answers.workers) && (answers.entity === 'llc' || answers.entity === 'corporation');
    case 'exempt':
      return Boolean(answers.workers) && answers.entity === 'nonprofit';
    case 'activity':
      if (!answers.workers) {
        return false;
      }
      if (answers.entity === 'llc' || answers.entity === 'corporation') {
        return Boolean(answers.tax);
      }
      if (answers.entity === 'nonprofit') {
        return Boolean(answers.exempt);
      }
      return true;
    case 'offers':
      return Boolean(answers.activity);
    case 'salesTax':
      return Boolean(answers.offers);
    case 'extraStates':
      return Boolean(answers.salesTax);
    case 'extraStateList':
      return answers.extraStates === 'yes';
    case 'bank':
      return answers.extraStates === 'no' || filled(answers.extraStateList);
    default:
      return false;
  }
};

const isAnswered = (id: BusinessQuestionId, answers: BusinessCreationAnswers): boolean => {
  const value = answers[id];
  if (id === 'state') {
    return STATE_NAME.has(String(value || ''));
  }
  if (id === 'homeCountry') {
    return FORMATION_COUNTRY_CODES.includes(String(value || '').toUpperCase());
  }
  return filled(typeof value === 'string' ? value : undefined);
};

const choice = (
  language: FormationLanguage,
  value: string,
  label: Pair,
  detail: Pair,
): BusinessQuestionOption => ({
  value,
  label: pair(language, label),
  detail: pair(language, detail),
});

const presentQuestion = (
  id: BusinessQuestionId,
  answers: BusinessCreationAnswers,
  language: FormationLanguage,
): BusinessQuestion => {
  const stateName = formationStateName(answers.state);
  const foreign = answers.origin === 'foreign';
  const sole = answers.entity === 'sole-prop';
  switch (id) {
    case 'entity':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'What kind of organization are you creating?', '¿Qué tipo de organización está creando?'),
        help: tx(
          language,
          'Formation services start here. The state filing, the tax default, and the later reports all depend on this answer.',
          'Los servicios de formación empiezan aquí. La presentación estatal, el impuesto por defecto y los reportes posteriores dependen de esta respuesta.',
        ),
        options: [
          choice(language, 'llc', ['Limited liability company', 'Sociedad de responsabilidad limitada'], ['Owners are usually not personally on the hook for company debts. Most small businesses start with an LLC.', 'Los dueños normalmente no responden con su dinero personal por las deudas de la empresa. La mayoría de los negocios pequeños empiezan con una LLC.']),
          choice(language, 'corporation', ['Corporation', 'Corporación'], ['Owned by shareholders and run by a board. It has more formal records than an LLC.', 'La poseen accionistas y la dirige un directorio. Exige más registros formales que una LLC.']),
          choice(language, 'nonprofit', ['Nonprofit', 'Organización sin fines de lucro'], ['A corporation that may later apply for federal tax-exempt status.', 'Una corporación que después puede pedir la exención fiscal federal.']),
          choice(language, 'partnership', ['Partnership', 'Sociedad'], ['Two or more people share the business without forming a corporation.', 'Dos o más personas comparten el negocio sin formar una corporación.']),
          choice(language, 'sole-prop', ['Sole proprietorship', 'Empresa unipersonal'], ['You operate under your own name, or a DBA, without creating a separate company.', 'Opera con su propio nombre, o un DBA, sin crear una empresa separada.']),
        ],
      };
    case 'origin':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Where will this company be created?', '¿Dónde se creará esta empresa?'),
        help: tx(
          language,
          'The home place sets the filing office, the tax number, and the later reports. Operating somewhere else is a separate registration, not a second company.',
          'El lugar de origen define la oficina de presentación, el número fiscal y los reportes posteriores. Operar en otro lugar es un registro aparte, no una segunda empresa.',
        ),
        options: [
          choice(language, 'us', ['In a U.S. state', 'En un estado de EE.UU.'], ['You will file with that state’s business office.', 'Presentará ante la oficina de negocios de ese estado.']),
          choice(language, 'territory', ['In a U.S. territory', 'En un territorio de EE.UU.'], ['Puerto Rico, Guam, the U.S. Virgin Islands, American Samoa, or the Northern Mariana Islands file with that territory’s own office.', 'Puerto Rico, Guam, las Islas Vírgenes de EE.UU., Samoa Americana o las Islas Marianas del Norte presentan ante la oficina de ese territorio.']),
          choice(language, 'foreign', ['In another country', 'En otro país'], ['You will form the company under that country’s registry. A local professional files it. NextCompli keeps the checklist.', 'Formará la empresa en el registro de ese país. Un profesional local la presenta. NextCompli guarda la lista.']),
        ],
      };
    case 'homeCountry':
      return {
        id,
        kind: 'country',
        prompt: tx(language, 'Which country will you form the company in?', '¿En qué país va a formar la empresa?'),
        help: tx(
          language,
          'Pick the country whose registry will create the company. If you will also have an office or employees somewhere else, you will name those places later.',
          'Elija el país cuyo registro creará la empresa. Si también tendrá oficina o empleados en otro lugar, nombrará esos lugares después.',
        ),
        placeholder: tx(language, 'Choose a country…', 'Elija un país…'),
      };
    case 'stage':
      return {
        id,
        kind: 'choice',
        prompt: sole
          ? tx(language, 'Have you filed this name as an assumed name?', '¿Ya presentó este nombre como nombre comercial?')
          : foreign
            ? tx(language, 'Have you registered the company with a U.S. state?', '¿Ya registró la empresa ante un estado de EE.UU.?')
            : tx(language, 'Have you filed the formation paperwork with the state?', '¿Ya presentó los documentos de formación ante el estado?'),
        help: tx(
          language,
          'This decides whether the next steps are “file it” or “save the approval you already have.”',
          'Esto decide si los próximos pasos son “preséntelo” o “guarde la aprobación que ya tiene.”',
        ),
        options: [
          choice(language, 'not-filed', ['Not yet', 'Todavía no'], ['The plan starts with a name search and the filing itself.', 'El plan empieza con la búsqueda del nombre y la presentación.']),
          choice(language, 'pending', ['Filed, waiting on the state', 'Presentado, esperando al estado'], ['Keep the receipt. The approval step stays waiting until the state accepts it.', 'Guarde el recibo. El paso de aprobación queda en espera hasta que el estado lo acepte.']),
          choice(language, 'approved', ['Already approved', 'Ya aprobado'], ['Save the stamped document and move on to the tax ID, bank, and licenses.', 'Guarde el documento sellado y siga con el ID fiscal, el banco y las licencias.']),
        ],
      };
    case 'state':
      return {
        id,
        kind: 'state',
        prompt: sole
          ? tx(language, 'Which state will you operate in?', '¿En qué estado va a operar?')
          : answers.origin === 'territory'
            ? tx(language, 'Which territory will you form the company in?', '¿En qué territorio va a formar la empresa?')
            : foreign
              ? tx(language, 'Which state will you register in?', '¿En qué estado se va a registrar?')
              : tx(language, 'Which state will you form the company in?', '¿En qué estado va a formar la empresa?'),
        help: tx(
          language,
          'The state sets the form name, the registered-agent rule, and whether a notice or first report is due.',
          'El estado define el nombre del formulario, la regla del agente registrado y si hay un aviso o un primer reporte.',
        ),
        placeholder: tx(language, 'Choose a state…', 'Elija un estado…'),
      };
    case 'legalName':
      return {
        id,
        kind: 'text',
        prompt: sole
          ? tx(language, 'What name will customers see?', '¿Qué nombre verán los clientes?')
          : tx(language, 'What is the legal business name?', '¿Cuál es la razón social?'),
        help: sole
          ? tx(language, 'Use the name on the sign, the website, and the invoices.', 'Use el nombre del letrero, del sitio y de las facturas.')
          : tx(language, 'Use the name you will put on the state filing. The bank and the IRS letter must match it.', 'Use el nombre que pondrá en la presentación estatal. El banco y la carta del IRS deben coincidir.'),
        placeholder: sole
          ? tx(language, 'Name customers will see', 'Nombre que verán los clientes')
          : tx(language, 'Legal name, including LLC or Inc. if you will use it', 'Razón social, con LLC o Inc. si la va a usar'),
      };
    case 'useDba':
      return {
        id,
        kind: 'choice',
        prompt: sole
          ? tx(language, 'Is that name different from your personal legal name?', '¿Ese nombre es distinto de su nombre legal personal?')
          : tx(language, 'Will you also use a trade name?', '¿También usará un nombre comercial?'),
        help: tx(
          language,
          'A trade name is a DBA. Many states or counties require a filing before you can open a bank account in that name.',
          'Un nombre comercial es un DBA. Muchos estados o condados exigen una presentación antes de abrir una cuenta bancaria con ese nombre.',
        ),
        options: [
          choice(language, 'yes', ['Yes', 'Sí'], ['The plan adds an assumed-name filing.', 'El plan añade la presentación del nombre comercial.']),
          choice(language, 'no', ['No', 'No'], ['You will use only the legal name.', 'Usará solo la razón social.']),
        ],
      };
    case 'dbaName':
      return {
        id,
        kind: 'text',
        prompt: tx(language, 'What is the trade name?', '¿Cuál es el nombre comercial?'),
        help: tx(language, 'This is the DBA customers will see. It is not a substitute for the legal name on the state filing.', 'Este es el DBA que verán los clientes. No sustituye la razón social de la presentación estatal.'),
        placeholder: tx(language, 'Trade name', 'Nombre comercial'),
      };
    case 'owners':
      return {
        id,
        kind: 'choice',
        prompt: answers.entity === 'corporation'
          ? tx(language, 'How many shareholders?', '¿Cuántos accionistas?')
          : tx(language, 'How many owners?', '¿Cuántos dueños?'),
        help: tx(
          language,
          'One owner and several owners change the default tax treatment and the governing document.',
          'Un dueño o varios cambian el tratamiento fiscal por defecto y el documento de gobierno.',
        ),
        options: [
          choice(language, 'one', ['One', 'Uno'], ['A single-owner LLC is usually taxed on that owner’s return unless you elect otherwise.', 'Una LLC de un solo dueño normalmente tributa en la declaración de esa persona, salvo que elija otra cosa.']),
          choice(language, 'many', ['Two or more', 'Dos o más'], ['The default for an LLC or partnership with two or more owners is partnership tax treatment.', 'El defecto de una LLC o sociedad con dos o más dueños es el tratamiento de sociedad.']),
        ],
      };
    case 'management':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Who will run the LLC?', '¿Quién dirigirá la LLC?'),
        help: tx(
          language,
          'Member-managed means the owners run it. Manager-managed means the owners appoint one or more managers. The state filing usually asks this.',
          'Gestionada por miembros significa que la dirigen los dueños. Gestionada por gerentes significa que los dueños nombran a uno o más gerentes. La presentación estatal suele preguntarlo.',
        ),
        options: [
          choice(language, 'members', ['The owners', 'Los dueños'], ['Member-managed.', 'Gestionada por miembros.']),
          choice(language, 'managers', ['Appointed managers', 'Gerentes designados'], ['Manager-managed. Name the managers in the operating agreement.', 'Gestionada por gerentes. Nombre a los gerentes en el acuerdo operativo.']),
        ],
      };
    case 'agent':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Who will receive lawsuits and state notices?', '¿Quién recibirá las demandas y los avisos del estado?'),
        help: tx(
          language,
          `Every state-filed company needs a registered agent with a street address in ${stateName}, available during business hours.`,
          `Toda empresa presentada ante el estado necesita un agente registrado con domicilio en ${stateName}, disponible en horario hábil.`,
        ),
        options: [
          choice(language, 'owner', ['An owner at a street address in that state', 'Un dueño con domicilio en ese estado'], ['The address is public on many state sites. A mailbox is not enough.', 'La dirección es pública en muchos sitios estatales. Un apartado postal no basta.']),
          choice(language, 'service', ['A registered-agent service', 'Un servicio de agente registrado'], ['The service’s address goes on the filing. Keep their renewal on the calendar.', 'La dirección del servicio va en la presentación. Ponga su renovación en el calendario.']),
          choice(language, 'later', ['Not chosen yet', 'Aún no elegido'], ['The filing step stays blocked until an agent is named.', 'El paso de presentación sigue bloqueado hasta nombrar un agente.']),
        ],
      };
    case 'workers':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Will you pay anyone in the next 12 months?', '¿Pagará a alguien en los próximos 12 meses?'),
        help: tx(
          language,
          'Employees require an EIN, state payroll accounts, and workers’ compensation. Contractors still need a W-9. Owners alone can often wait on payroll accounts.',
          'Los empleados exigen un EIN, cuentas estatales de nómina y compensación laboral. Los contratistas igual necesitan un W-9. Si solo hay dueños, las cuentas de nómina pueden esperar.',
        ),
        options: [
          choice(language, 'employees', ['Yes, W-2 employees', 'Sí, empleados W-2'], ['Adds payroll registration and workers’ compensation.', 'Añade el registro de nómina y la compensación laboral.']),
          choice(language, 'contractors', ['Contractors only', 'Solo contratistas'], ['Collect a W-9. Do not treat them as employees.', 'Pida un W-9. No los trate como empleados.']),
          choice(language, 'owners', ['Only the owners', 'Solo los dueños'], ['No payroll account yet. Change this answer before you pay a contractor. A contractor outside the United States gives you IRS Form W-8BEN instead of a W-9.', 'Aún no hay cuenta de nómina. Cambie esta respuesta antes de pagar a un contratista. Un contratista fuera de Estados Unidos entrega el Formulario W-8BEN del IRS, no un W-9.']),
        ],
      };
    case 'tax':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'How should profits be taxed?', '¿Cómo deben tributar las ganancias?'),
        help: tx(
          language,
          'The default needs no extra IRS form, and the profit goes on the owners’ personal returns. An S corporation election is IRS Form 2553. A C corporation election is IRS Form 8832 and taxes the profit at the company instead. Either election has a deadline. Franchise tax in the state where you do business still applies. This is a planning choice, not tax advice.',
          'El defecto no necesita otro formulario del IRS, y la ganancia va en las declaraciones personales de los dueños. La elección de corporación S es el Formulario 2553 del IRS. La elección de corporación C es el Formulario 8832 del IRS y grava la ganancia en la empresa. Cualquiera de las dos tiene un plazo. El impuesto de franquicia del estado donde opera sigue aplicando. Es una decisión de planificación, no un consejo fiscal.',
        ),
        options: answers.entity === 'corporation'
          ? [
            choice(language, 'default', ['Stay a C corporation', 'Seguir como corporación C'], ['This is the default for a corporation. Profits can be taxed at the company and again when paid out.', 'Es el defecto de una corporación. Las ganancias pueden tributar en la empresa y otra vez al repartirse.']),
            choice(language, 's-corp', ['Elect S corporation', 'Elegir corporación S'], ['File IRS Form 2553. Shareholders then report the profit on their own returns.', 'Presente el Formulario 2553 del IRS. Los accionistas reportan la ganancia en sus propias declaraciones.']),
          ]
          : [
            choice(language, 'default', ['Keep the default', 'Mantener el defecto'], [answers.owners === 'many' ? 'Partnership treatment. The company files an information return and the owners report the profit.' : 'One owner: the profit goes on that owner’s return. No separate company tax return.', answers.owners === 'many' ? 'Tratamiento de sociedad. La empresa presenta una declaración informativa y los dueños reportan la ganancia.' : 'Un dueño: la ganancia va en la declaración de esa persona. No hay declaración separada de la empresa.']),
            choice(language, 's-corp', ['S corporation election', 'Elección de corporación S'], ['File IRS Form 2553 after the company has an EIN.', 'Presente el Formulario 2553 del IRS cuando la empresa tenga EIN.']),
            choice(language, 'c-corp', ['C corporation election', 'Elección de corporación C'], ['File IRS Form 8832. Profit is taxed at the company instead of flowing onto the owners’ personal returns. Franchise tax where you do business still applies.', 'Presente el Formulario 8832 del IRS. La ganancia tributa en la empresa en vez de pasar a las declaraciones personales de los dueños. El impuesto de franquicia donde opera sigue aplicando.']),
          ],
      };
    case 'exempt':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Will you apply for federal tax-exempt status?', '¿Pedirá la exención fiscal federal?'),
        help: tx(
          language,
          'Forming the nonprofit with the state does not make it tax-exempt. Exemption is a separate IRS application.',
          'Formar la organización ante el estado no la hace exenta. La exención es una solicitud aparte ante el IRS.',
        ),
        options: [
          choice(language, 'apply', ['Yes, charity', 'Sí, caridad'], ['The usual path is federal charity status. The plan adds the IRS application after the EIN.', 'El camino habitual es el estatus federal de caridad. El plan añade la solicitud al IRS después del EIN.']),
          choice(language, 'c4', ['Social welfare', 'Bienestar social'], ['A social-welfare organization uses a different IRS application than a charity.', 'Una organización de bienestar social usa una solicitud del IRS distinta de la de una caridad.']),
          choice(language, 'c6', ['Business league', 'Liga empresarial'], ['A chamber or association uses the business-league application.', 'Una cámara o asociación usa la solicitud de liga empresarial.']),
          choice(language, 'later', ['Not yet', 'Todavía no'], ['You can add the application later. State formation still comes first.', 'Puede añadir la solicitud después. La formación estatal sigue primero.']),
        ],
      };
    case 'activity':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'What will the business mainly do?', '¿Qué hará principalmente el negocio?'),
        help: tx(
          language,
          'Licenses follow the activity and the city, not just the state filing. A formation service’s license report is this same question.',
          'Las licencias siguen la actividad y la ciudad, no solo la presentación estatal. El reporte de licencias de un servicio de formación es esta misma pregunta.',
        ),
        options: [
          choice(language, 'professional', ['Licensed professional work', 'Trabajo profesional con licencia'], ['Law, medicine, accounting, engineering, or real estate brokerage.', 'Derecho, medicina, contabilidad, ingeniería o corretaje inmobiliario.']),
          choice(language, 'food', ['Food, alcohol, or lodging', 'Comida, alcohol o hospedaje'], ['Health permits and, for alcohol, a separate license.', 'Permisos de salud y, para alcohol, una licencia aparte.']),
          choice(language, 'goods', ['Sell physical goods', 'Vender bienes físicos'], ['A seller’s permit is likely if the state taxes those sales.', 'Es probable un permiso de vendedor si el estado grava esas ventas.']),
          choice(language, 'field', ['Construction, trucking, or job sites', 'Construcción, transporte o sitios de obra'], ['Contractor registration or transportation permits may apply.', 'Puede aplicar el registro de contratista o permisos de transporte.']),
          choice(language, 'online', ['Online or home-based services', 'Servicios en línea o desde casa'], ['Still check the city home-occupation rule.', 'Igual revise la regla municipal de oficio en casa.']),
          choice(language, 'other', ['Something else', 'Otra cosa'], ['The plan keeps a step to list the licenses for this activity.', 'El plan guarda un paso para listar las licencias de esta actividad.']),
        ],
      };
    case 'offers':
      return {
        id,
        kind: 'choice',
        prompt: tx(
          language,
          'Does this company sell a product, or own another company?',
          '¿Esta empresa vende un producto o es dueña de otra empresa?',
        ),
        help: tx(
          language,
          'A product or subscription is something this company sells. It is not its own company. A subsidiary is a separate company this one owns, with its own filing, tax number, and bank account. Executives keep one sanctions program on the company that sells the product.',
          'Un producto o suscripción es algo que vende esta empresa. No es una empresa propia. Una subsidiaria es otra empresa de la que esta es dueña, con su propia presentación, número fiscal y cuenta bancaria. Los ejecutivos mantienen un solo programa de sanciones en la empresa que vende el producto.',
        ),
        options: [
          choice(language, 'company', ['Just this company', 'Solo esta empresa'], ['No separate product line and no owned company.', 'No hay una línea de producto aparte ni una empresa propia.']),
          choice(language, 'product', ['It sells a product or subscription', 'Vende un producto o suscripción'], ['The plan adds steps to name the product and screen its customers under this company.', 'El plan añade pasos para nombrar el producto y cribar a sus clientes bajo esta empresa.']),
          choice(language, 'subsidiary', ['It owns a separate company', 'Es dueña de otra empresa'], ['That company needs its own formation record. Do not reuse this filing.', 'Esa empresa necesita su propio expediente de formación. No reutilice esta presentación.']),
        ],
      };
    case 'salesTax':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Will you sell anything the state taxes?', '¿Venderá algo que el estado grave?'),
        help: tx(
          language,
          'Taxable goods usually need a seller’s permit before the first sale. Some services are taxed too. If you are not sure, the plan keeps a confirmation step.',
          'Los bienes gravados suelen necesitar un permiso de vendedor antes de la primera venta. Algunos servicios también tributan. Si no está seguro, el plan guarda un paso de confirmación.',
        ),
        options: [
          choice(language, 'yes', ['Yes', 'Sí'], ['Adds a seller’s permit step.', 'Añade el paso del permiso de vendedor.']),
          choice(language, 'no', ['No', 'No'], ['No seller’s permit from this answer.', 'Esta respuesta no añade permiso de vendedor.']),
          choice(language, 'unsure', ['Not sure', 'No estoy seguro'], ['Adds a step to confirm with the state tax office.', 'Añade un paso para confirmarlo con la oficina fiscal del estado.']),
        ],
      };
    case 'extraStates':
      return {
        id,
        kind: 'choice',
        prompt: tx(
          language,
          'Will you have an office, employees, or regular in-person work in another state, territory, or country?',
          '¿Tendrá oficina, empleados o trabajo presencial habitual en otro estado, territorio o país?',
        ),
        help: tx(
          language,
          'Each extra place wants its own registration. Doing the work in a state other than the formation state counts, even without a second office. That state can still charge franchise or margin tax. A website alone usually does not require it. Name every place, including a U.S. state if the company was formed abroad.',
          'Cada lugar adicional pide su propio registro. Hacer el trabajo en un estado distinto del de formación cuenta, aunque no haya una segunda oficina. Ese estado puede seguir cobrando impuesto de franquicia o de margen. Un sitio web por sí solo normalmente no lo exige. Nombre cada lugar, incluido un estado de EE.UU. si la empresa se formó en el extranjero.',
        ),
        options: [
          choice(language, 'no', ['No, only the home place', 'No, solo el lugar de origen'], ['No extra registration from this answer.', 'Esta respuesta no añade otro registro.']),
          choice(language, 'yes', ['Yes', 'Sí'], ['You will name the other places next.', 'Después nombrará los otros lugares.']),
        ],
      };
    case 'extraStateList':
      return {
        id,
        kind: 'text',
        prompt: tx(language, 'Which other places?', '¿Qué otros lugares?'),
        help: tx(language, 'List each state, territory, or country where you will have an office, employees, or regular in-person work.', 'Liste cada estado, territorio o país donde tendrá oficina, empleados o trabajo presencial habitual.'),
        placeholder: tx(language, 'For example: Oklahoma, Venezuela', 'Por ejemplo: Oklahoma, Venezuela'),
      };
    case 'bank':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Is there already a bank account in the business name?', '¿Ya hay una cuenta bancaria a nombre del negocio?'),
        help: tx(
          language,
          'Keep business money separate from personal money. Banks usually ask for the state approval and the EIN.',
          'Separe el dinero del negocio del dinero personal. Los bancos suelen pedir la aprobación del estado y el EIN.',
        ),
        options: [
          choice(language, 'no', ['Not yet', 'Todavía no'], ['The plan adds “open the account” after the EIN.', 'El plan añade “abrir la cuenta” después del EIN.']),
          choice(language, 'yes', ['Yes', 'Sí'], ['The plan asks you to keep proof the account is in the legal name.', 'El plan pide guardar prueba de que la cuenta está a nombre legal.']),
        ],
      };
    default:
      return {
        id: 'entity',
        kind: 'choice',
        prompt: '',
        help: '',
      };
  }
};

export const nextBusinessQuestion = (
  answers: BusinessCreationAnswers,
  language: FormationLanguage = 'en',
): BusinessQuestion | null => {
  const id = QUESTION_ORDER.find((questionId) => questionApplies(questionId, answers) && !isAnswered(questionId, answers));
  return id ? presentQuestion(id, answers, language) : null;
};

export const businessQuestionNumber = (answers: BusinessCreationAnswers): number => (
  QUESTION_ORDER.filter((id) => (
    id !== 'legalName'
    && questionApplies(id, answers)
    && isAnswered(id, answers)
  )).length + 1
);

export const isBusinessCreationComplete = (answers: BusinessCreationAnswers): boolean => (
  Boolean(answers.entity) && nextBusinessQuestion(answers) === null
);

const prune = (answers: BusinessCreationAnswers): BusinessCreationAnswers => {
  const next: BusinessCreationAnswers = { ...answers };
  QUESTION_ORDER.forEach((id) => {
    // The company name is saved on Which company before this interview starts.
    if (id === 'legalName' && filled(next.legalName)) {
      return;
    }
    if (!questionApplies(id, next)) {
      delete next[id];
    }
  });
  return next;
};

export const answerBusinessQuestion = (
  answers: BusinessCreationAnswers,
  id: BusinessQuestionId,
  value: string,
): BusinessCreationAnswers => prune({ ...answers, [id]: value.trim() } as BusinessCreationAnswers);

export const retreatBusinessAnswers = (answers: BusinessCreationAnswers): BusinessCreationAnswers => {
  const last = [...QUESTION_ORDER].reverse().find((id) => (
    id !== 'legalName' && questionApplies(id, answers) && isAnswered(id, answers)
  ));
  if (!last) {
    return answers;
  }
  const next: BusinessCreationAnswers = { ...answers };
  delete next[last];
  return prune(next);
};

export const businessCreationAnswerLines = (
  answers: BusinessCreationAnswers,
  language: FormationLanguage,
): string[] => {
  const lines: string[] = [];
  const entityLabels: Record<string, Pair> = {
    llc: ['Limited liability company (LLC)', 'Sociedad de responsabilidad limitada (LLC)'],
    corporation: ['Corporation', 'Corporación'],
    nonprofit: ['Nonprofit', 'Organización sin fines de lucro'],
    partnership: ['Partnership', 'Sociedad'],
    'sole-prop': ['Sole proprietorship', 'Empresa unipersonal'],
  };
  if (answers.entity) {
    lines.push(pair(language, entityLabels[answers.entity]));
  }
  if (answers.origin) {
    lines.push(answers.origin === 'foreign'
      ? tx(language, 'Created outside the United States', 'Creada fuera de Estados Unidos')
      : tx(language, 'Created in a U.S. state', 'Creada en un estado de EE.UU.'));
  }
  if (answers.state) {
    lines.push(formationStateName(answers.state));
  }
  if (filled(answers.legalName)) {
    lines.push(String(answers.legalName).trim());
  }
  if (answers.useDba === 'yes' && filled(answers.dbaName)) {
    lines.push(tx(language, `Trade name (DBA): ${answers.dbaName}`, `Nombre comercial (DBA): ${answers.dbaName}`));
  }
  if (answers.workers === 'employees') {
    lines.push(tx(language, 'Will hire W-2 employees', 'Contratará empleados W-2'));
  }
  if (answers.tax === 's-corp') {
    lines.push(tx(language, 'S corporation tax election', 'Elección fiscal de corporación S'));
  }
  if (answers.extraStates === 'yes' && filled(answers.extraStateList)) {
    lines.push(tx(language, `Also operating in ${answers.extraStateList}`, `También opera en ${answers.extraStateList}`));
  }
  return lines;
};

const step = (
  language: FormationLanguage,
  spec: {
    id: string;
    title: Pair;
    action: Pair;
    hint: Pair;
    priority?: ComplianceItemPriority;
    dependsOn?: string[];
    planMin?: PricingTierId;
    sourceLabel?: Pair;
    sourceUrl?: string;
    docsLabel?: Pair;
    docsUrl?: string;
    waiting?: Pair[];
    guides?: Pair[];
    proof?: Pair;
  },
): ComplianceChecklistItemDef => {
  const verifySteps: ComplianceVerifyStepDef[] | undefined = spec.proof
    ? [
      {
        id: `${spec.id}-do`,
        kind: 'do',
        text: spec.guides?.length
          ? pair(language, ['Do the numbered steps, then save the proof.', 'Haga los pasos numerados y luego guarde la prueba.'])
          : pair(language, spec.action),
        evidenceHint: pair(language, spec.hint),
      },
      {
        id: `${spec.id}-proof`,
        kind: 'proof',
        text: pair(language, spec.proof),
        evidenceHint: pair(language, spec.hint),
      },
    ]
    : undefined;
  return {
    id: spec.id,
    title: pair(language, spec.title),
    action: pair(language, spec.action),
    evidenceHint: pair(language, spec.hint),
    priority: spec.priority,
    dependsOn: spec.dependsOn,
    planMin: spec.planMin,
    sourceLabel: spec.sourceLabel ? pair(language, spec.sourceLabel) : undefined,
    sourceUrl: spec.sourceUrl,
    docsLabel: spec.docsLabel ? pair(language, spec.docsLabel) : undefined,
    docsUrl: spec.docsUrl,
    waitingSteps: spec.waiting?.map((row) => pair(language, row)),
    actionSteps: spec.guides?.map((row) => pair(language, row)),
    verifySteps,
    completeRequires: spec.proof ? 'evidence' : undefined,
  };
};

const SBA_REGISTER = 'https://www.sba.gov/business-guide/launch-your-business/register-your-business';
const SBA_LICENSES = 'https://www.sba.gov/business-guide/launch-your-business/apply-licenses-permits';
const SBA_BANK = 'https://www.sba.gov/business-guide/launch-your-business/open-business-bank-account';
const SBA_INSURANCE = 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance';
const SBA_STRUCTURE = 'https://www.sba.gov/business-guide/launch-your-business/choose-business-structure';
const SBA_MARKET = 'https://www.sba.gov/business-guide/grow-your-business/market-your-business';
const IRS_EIN = 'https://www.irs.gov/businesses/small-businesses-self-employed/get-an-employer-identification-number';
const IRS_START_CHECKLIST = 'https://www.irs.gov/businesses/small-businesses-self-employed/checklist-for-starting-a-business';
const GOOGLE_BUSINESS_PROFILE = 'https://business.google.com/';
const GOOGLE_MX = 'https://support.google.com/a/answer/140034';
const GOOGLE_SPF = 'https://support.google.com/a/answer/33786';
const GOOGLE_DKIM = 'https://support.google.com/a/answer/174124';
const GOOGLE_DMARC = 'https://support.google.com/a/answer/2466580';

const companyEmailItems = (language: FormationLanguage, after: string): ComplianceChecklistItemDef[] => [
  step(language, {
    id: 'business-email',
    title: ['Create the company mailbox', 'Cree el buzón de la empresa'],
    action: ['Create a mailbox on the company domain, such as hello@ or the owners’ names. Stop using a free personal inbox for invoices, contracts, and customer mail.', 'Cree un buzón en el dominio de la empresa, como hola@ o los nombres de los dueños. Deje de usar un correo personal gratuito para facturas, contratos y clientes.'],
    hint: ['The mailbox address', 'La dirección del buzón'],
    priority: 'required',
    dependsOn: [after],
    guides: [
      ['Create the mailbox on the company domain.', 'Cree el buzón en el dominio de la empresa.'],
      ['On a phone or mail app, sign in with that login. A send-as alias is not the account.', 'En el teléfono o la app de correo, entre con ese acceso. Un alias de envío no es la cuenta.'],
    ],
    sourceLabel: ['Google Workspace MX records', 'Registros MX de Google Workspace'],
    sourceUrl: GOOGLE_MX,
    proof: ['Save the mailbox address. Do not save the password here.', 'Guarde la dirección del buzón. No guarde la contraseña aquí.'],
  }),
  step(language, {
    id: 'business-email-mx',
    title: ['Point mail at that mailbox', 'Apunte el correo a ese buzón'],
    action: ['Set the domain’s mail exchange to the mailbox host, in the DNS panel the nameservers actually use. A record saved in the wrong panel does nothing.', 'Ponga el intercambio de correo del dominio en el host del buzón, en el panel DNS que usan los servidores de nombres. Un registro guardado en el panel equivocado no hace nada.'],
    hint: ['Mail-exchange record in the live DNS panel', 'Registro de intercambio de correo en el panel DNS en vivo'],
    priority: 'required',
    dependsOn: ['business-email'],
    guides: [
      ['Find the nameservers for the domain and open that DNS panel.', 'Encuentre los servidores de nombres del dominio y abra ese panel DNS.'],
      ['For Google Workspace, set priority 1 and host smtp.google.com.', 'Para Google Workspace, ponga prioridad 1 y host smtp.google.com.'],
      ['Remove an old mail host, such as WorkMail, so new mail does not split.', 'Quite un host de correo viejo, como WorkMail, para que el correo nuevo no se divida.'],
    ],
    sourceLabel: ['Google Workspace MX records', 'Registros MX de Google Workspace'],
    sourceUrl: GOOGLE_MX,
    proof: ['Save a screenshot of the mail-exchange record', 'Guarde una captura del registro de intercambio de correo'],
  }),
  step(language, {
    id: 'business-email-spf',
    title: ['Publish one SPF record', 'Publique un registro SPF'],
    action: ['Publish one SPF text record that lists the mailbox and every other system that sends as the domain. Two SPF records make both fail.', 'Publique un registro de texto SPF que liste el buzón y cada otro sistema que envía como el dominio. Dos registros SPF hacen fallar ambos.'],
    hint: ['The one SPF text record', 'El único registro de texto SPF'],
    priority: 'required',
    dependsOn: ['business-email-mx'],
    guides: [
      ['List every system that sends as the domain: the mailbox, and any app or marketing tool.', 'Liste cada sistema que envía como el dominio: el buzón y cualquier app o herramienta de marketing.'],
      ['Publish one text record on the domain. It starts with v=spf1. Do not add a second SPF record.', 'Publique un registro de texto en el dominio. Empieza con v=spf1. No añada un segundo registro SPF.'],
      ['If the mailbox is Google, include _spf.google.com. A record that only names Amazon or another sender makes the human mail fail.', 'Si el buzón es Google, incluya _spf.google.com. Un registro que solo nombra a Amazon u otro remitente hace fallar el correo de las personas.'],
      ['Include each other sender the same way. End with ~all.', 'Incluya cada otro remitente del mismo modo. Termine con ~all.'],
    ],
    sourceLabel: ['Google Workspace SPF', 'SPF de Google Workspace'],
    sourceUrl: GOOGLE_SPF,
    proof: ['Save a screenshot of the single SPF record', 'Guarde una captura del único registro SPF'],
  }),
  step(language, {
    id: 'business-email-dkim',
    title: ['Turn on DKIM and publish one key', 'Active DKIM y publique una clave'],
    action: ['Generate the DKIM key, publish it as one text record, then start authentication. Splitting the long key into two records, or never clicking Start authentication, is why this step has to be redone.', 'Genere la clave DKIM, publíquela como un registro de texto y luego inicie la autenticación. Partir la clave larga en dos registros, o no pulsar Iniciar autenticación, es por lo que hay que rehacer este paso.'],
    hint: ['One DKIM text record, and authentication started', 'Un registro de texto DKIM, y la autenticación iniciada'],
    priority: 'blocking',
    dependsOn: ['business-email-spf'],
    guides: [
      ['In Google Workspace open Admin, then Apps, Google Workspace, Gmail, Authenticate email. Pick the company domain. Click Generate new record.', 'En Google Workspace abra Admin, luego Apps, Google Workspace, Gmail, Autenticar correo. Elija el dominio de la empresa. Pulse Generar registro nuevo.'],
      ['The host name for Google is google._domainkey. The value is one long key.', 'El nombre de host para Google es google._domainkey. El valor es una clave larga.'],
      ['Add one text record at that name in the DNS panel the nameservers use. Several quoted pieces inside one record join. Two separate text records with the same name do not join, and DKIM fails. Do not add a second record.', 'Añada un registro de texto con ese nombre en el panel DNS que usan los servidores de nombres. Varios fragmentos entre comillas dentro de un registro se unen. Dos registros de texto separados con el mismo nombre no se unen, y DKIM falla. No añada un segundo registro.'],
      ['If another system also sends as this domain, add the keys it shows, under the names it shows. Do not paste those keys onto google._domainkey.', 'Si otro sistema también envía como este dominio, añada las claves que muestra, con los nombres que muestra. No pegue esas claves en google._domainkey.'],
      ['Go back and click Start authentication. Wait until it says it is authenticating. A generated key that was never started does not sign mail.', 'Vuelva y pulse Iniciar autenticación. Espere hasta que diga que está autenticando. Una clave generada que nunca se inició no firma el correo.'],
      ['Microsoft 365 is different: publish the two alias records the admin center shows, then turn signing on. Do not invent a Google text record for a Microsoft mailbox.', 'Microsoft 365 es distinto: publique los dos registros de alias que muestra el centro de administración y luego active la firma. No invente un registro de texto de Google para un buzón de Microsoft.'],
    ],
    sourceLabel: ['Turn on DKIM in Google Workspace', 'Active DKIM en Google Workspace'],
    sourceUrl: GOOGLE_DKIM,
    proof: ['Save a screenshot that authentication was started, plus the one DNS record', 'Guarde una captura de que la autenticación se inició, y el único registro DNS'],
  }),
  step(language, {
    id: 'business-email-dmarc',
    title: ['Publish DMARC at monitor only', 'Publique DMARC solo en vigilancia'],
    action: ['Publish one _dmarc text record at p=none with a report mailbox a person reads. Do not jump to quarantine or reject on the first day.', 'Publique un registro de texto _dmarc en p=none con un buzón de informes que una persona lea. No pase a cuarentena o rechazo el primer día.'],
    hint: ['The _dmarc record and the report mailbox', 'El registro _dmarc y el buzón de informes'],
    priority: 'required',
    dependsOn: ['business-email-dkim'],
    guides: [
      ['Add one text record named _dmarc in the same DNS panel.', 'Añada un registro de texto llamado _dmarc en el mismo panel DNS.'],
      ['Use v=DMARC1; p=none; rua=mailto: and a mailbox a person already reads.', 'Use v=DMARC1; p=none; rua=mailto: y un buzón que una persona ya revisa.'],
      ['Leave quarantine and reject for later, after a test message passes.', 'Deje la cuarentena y el rechazo para después, cuando un mensaje de prueba pase.'],
    ],
    sourceLabel: ['Google Workspace DMARC', 'DMARC de Google Workspace'],
    sourceUrl: GOOGLE_DMARC,
    proof: ['Save a screenshot of the _dmarc record', 'Guarde una captura del registro _dmarc'],
  }),
  step(language, {
    id: 'business-email-check',
    title: ['Confirm a sent message passed SPF and DKIM', 'Confirme que un mensaje enviado pasó SPF y DKIM'],
    action: ['Send from the company address and open Show original. Both SPF and DKIM must pass. A fail means the DNS is in the wrong panel, the DKIM key was split, or authentication was never started.', 'Envíe desde la dirección de la empresa y abra Mostrar original. SPF y DKIM deben pasar. Un fallo significa que el DNS está en el panel equivocado, que la clave DKIM se partió, o que la autenticación nunca se inició.'],
    hint: ['Message original showing spf=pass and dkim=pass', 'Original del mensaje con spf=pass y dkim=pass'],
    priority: 'blocking',
    dependsOn: ['business-email-dmarc'],
    guides: [
      ['Send a message from the company address to a Gmail account you can open.', 'Envíe un mensaje desde la dirección de la empresa a una cuenta de Gmail que pueda abrir.'],
      ['Open the message, then Show original.', 'Abra el mensaje y luego Mostrar original.'],
      ['You need spf=pass and dkim=pass for the company domain. If either says fail, go back to the DKIM step before you print cards or send invoices.', 'Necesita spf=pass y dkim=pass para el dominio de la empresa. Si alguno dice fail, vuelva al paso DKIM antes de imprimir tarjetas o enviar facturas.'],
    ],
    sourceLabel: ['Gmail email sender guidelines', 'Guías de remitente de Gmail'],
    sourceUrl: 'https://support.google.com/mail/answer/81126',
    proof: ['Save the message original. A DNS screenshot alone is not proof.', 'Guarde el original del mensaje. Una captura de DNS sola no es prueba.'],
  }),
];
const IRS_2553 = 'https://www.irs.gov/forms-pubs/about-form-2553';
const IRS_1023 = 'https://www.irs.gov/charities-non-profits/applying-for-tax-exempt-status';
const IRS_W9 = 'https://www.irs.gov/forms-pubs/about-form-w-9';
const FINCEN_BOI = 'https://www.fincen.gov/boi';
const TX_PORTAL = 'https://www.sos.texas.gov/corp/portal-access-guide.shtml';
const TX_FORM_205 = 'https://www.sos.texas.gov/corp/instructions/205.shtml';
const TX_FORMS = 'https://www.sos.texas.gov/corp/forms_boc.shtml';
const TX_CERTIFICATE = 'https://www.sos.texas.gov/corp/copies.shtml';
const TX_FRANCHISE = 'https://comptroller.texas.gov/taxes/franchise/';
const TX_SALES_PERMIT = 'https://comptroller.texas.gov/taxes/permit/';
const TX_PAYROLL = 'https://www.twc.texas.gov/businesses/unemployment-tax';
const TX_WORKERS_COMP = 'https://www.tdi.texas.gov/wc/employer/index.html';

type HowTo = { url: string; label: Pair };

const howTo = (url: string, en: string, es: string): HowTo => ({ url, label: [en, es] });

const SBA_REGISTER_HOW = howTo(SBA_REGISTER, 'SBA — register your business', 'SBA — registre su negocio');
const SBA_LICENSE_HOW = howTo(SBA_LICENSES, 'SBA — licenses and permits', 'SBA — licencias y permisos');

const nameSearchHowTo = (state: string | undefined): HowTo => (
  state === 'TX'
    ? howTo(TX_PORTAL, 'Texas Secretary of State portal — create an account and search the name', 'Portal del Secretario de Estado de Texas — cree una cuenta y busque el nombre')
    : SBA_REGISTER_HOW
);

const filingHowTo = (state: string | undefined, entity: string | undefined): HowTo => {
  if (state === 'TX' && entity === 'llc') {
    return howTo(TX_FORM_205, 'Texas Secretary of State — Form 205, form the company', 'Secretario de Estado de Texas — Formulario 205, formar la empresa');
  }
  if (state === 'TX') {
    return howTo(TX_PORTAL, 'Texas Secretary of State portal — file the company', 'Portal del Secretario de Estado de Texas — presente la empresa');
  }
  if (state === 'NY') {
    return howTo('https://dos.ny.gov/limited-liability-company', 'New York Department of State — form the company', 'Departamento de Estado de Nueva York — formar la empresa');
  }
  if (state === 'CA') {
    return howTo('https://www.sos.ca.gov/business-programs/business-entities', 'California Secretary of State — form the company', 'Secretario de Estado de California — formar la empresa');
  }
  return SBA_REGISTER_HOW;
};

const agentHowTo = (state: string | undefined): HowTo => (
  state === 'TX'
    ? howTo(TX_FORM_205, 'Texas Form 205 — registered agent and street address', 'Formulario 205 de Texas — agente registrado y domicilio')
    : SBA_REGISTER_HOW
);

const reportHowTo = (state: string | undefined): HowTo => (
  state === 'TX'
    ? howTo(TX_FRANCHISE, 'Texas Comptroller — franchise tax report, due May 15', 'Contralor de Texas — reporte de franquicia, vence el 15 de mayo')
    : state === 'CA'
      ? howTo('https://www.sos.ca.gov/business-programs/business-entities/statements', 'California — Statement of Information', 'California — Statement of Information')
      : SBA_REGISTER_HOW
);

const salesTaxHowTo = (state: string | undefined): HowTo => (
  state === 'TX'
    ? howTo(TX_SALES_PERMIT, 'Texas Comptroller — apply for a sales tax permit', 'Contralor de Texas — solicite un permiso de impuesto sobre ventas')
    : SBA_LICENSE_HOW
);

const payrollHowTo = (state: string | undefined): HowTo => (
  state === 'TX'
    ? howTo(TX_PAYROLL, 'Texas Workforce Commission — unemployment tax account', 'Comisión de la Fuerza Laboral de Texas — cuenta de impuesto de desempleo')
    : SBA_REGISTER_HOW
);

const workersCompHowTo = (state: string | undefined): HowTo => (
  state === 'TX'
    ? howTo(TX_WORKERS_COMP, 'Texas Department of Insurance — workers’ compensation', 'Departamento de Seguros de Texas — compensación laboral')
    : howTo(SBA_INSURANCE, 'SBA — business insurance', 'SBA — seguro del negocio')
);

const foreignCountryPillars = (
  answers: BusinessCreationAnswers,
  language: FormationLanguage,
): CompliancePillarDef[] => {
  const country = formationCountryName(answers.homeCountry, language);
  const code = String(answers.homeCountry || '').toUpperCase();
  const restricted = SANCTIONS_REVIEW_COUNTRIES.has(code);
  const registry = code === 'VE'
    ? tx(language, 'the commercial registry (Registro Mercantil)', 'el Registro Mercantil')
    : tx(language, `the company registry in ${country}`, `el registro de empresas de ${country}`);
  const taxId = code === 'VE'
    ? tx(language, 'the tax information number (RIF) with the tax office (SENIAT)', 'el Registro de Información Fiscal (RIF) ante el SENIAT')
    : tx(language, `the business tax number in ${country}`, `el número fiscal del negocio en ${country}`);
  const formation: ComplianceChecklistItemDef[] = [];
  if (restricted) {
    formation.push(step(language, {
      id: 'sanctions-review',
      title: ['Review U.S. sanctions before any filing or payment', 'Revise las sanciones de EE.UU. antes de cualquier presentación o pago'],
      action: [`${country} is on a U.S. sanctions list. A U.S. person needs a sanctions review before forming, paying, or operating there. NextCompli keeps this checklist. It does not file, and it does not bypass that review.`, `${country} está en una lista de sanciones de EE.UU. Una persona de EE.UU. necesita una revisión de sanciones antes de formar, pagar u operar allí. NextCompli guarda esta lista. No presenta, y no evita esa revisión.`],
      hint: ['Written sanctions review', 'Revisión escrita de sanciones'],
      priority: 'blocking',
      proof: ['Save the review. Do not file until it says the step is allowed.', 'Guarde la revisión. No presente hasta que diga que el paso está permitido.'],
    }));
  }
  formation.push(step(language, {
    id: 'name-availability',
    title: ['Confirm the name with the local registry', 'Confirme el nombre en el registro local'],
    action: [`Search ${registry} for ${answers.legalName} before you file. A search does not reserve the name.`, `Busque ${answers.legalName} en ${registry} antes de presentar. La búsqueda no reserva el nombre.`],
    hint: ['Name-search result', 'Resultado de la búsqueda del nombre'],
    priority: 'blocking',
    proof: ['Save the search result', 'Guarde el resultado de la búsqueda'],
  }));
  formation.push(step(language, {
    id: 'formation-filing',
    title: [`File with the registry in ${country}`, `Presente ante el registro de ${country}`],
    action: [`A lawyer or formation service in ${country} files ${answers.legalName} with ${registry}. NextCompli stores the receipt and the stamped approval. It does not file with that government.`, `Un abogado o un servicio de formación en ${country} presenta ${answers.legalName} ante ${registry}. NextCompli guarda el recibo y la aprobación sellada. No presenta ante ese gobierno.`],
    hint: ['Stamped registry approval', 'Aprobación sellada del registro'],
    priority: 'blocking',
    dependsOn: restricted ? ['sanctions-review', 'name-availability'] : ['name-availability'],
    proof: ['Save the stamped approval', 'Guarde la aprobación sellada'],
  }));
  formation.push(step(language, {
    id: 'local-tax-id',
    title: [`Get the tax number in ${country}`, `Obtenga el número fiscal en ${country}`],
    action: [`After the registry accepts the company, apply for ${taxId}. The responsible person uses their own identification. Do not type that number into this checklist.`, `Después de que el registro acepte la empresa, solicite ${taxId}. La persona responsable usa su propia identificación. No escriba ese número en esta lista.`],
    hint: ['Tax-number letter', 'Carta del número fiscal'],
    priority: 'blocking',
    dependsOn: ['formation-filing'],
    proof: ['Save the tax-number letter. Do not paste the number here.', 'Guarde la carta del número fiscal. No pegue el número aquí.'],
  }));
  const stay: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'business-bank',
      title: ['Open a bank account in the company name', 'Abra una cuenta bancaria a nombre de la empresa'],
      action: [`Open an account titled ${answers.legalName} in ${country} after the tax number exists. Keep company money separate from personal money.`, `Abra una cuenta a nombre de ${answers.legalName} en ${country} después de que exista el número fiscal. Separe el dinero de la empresa del dinero personal.`],
      hint: ['Bank letter in the company name', 'Carta del banco a nombre de la empresa'],
      priority: 'required',
      dependsOn: ['local-tax-id'],
      proof: ['Save the bank letter', 'Guarde la carta del banco'],
    }),
  ];
  if (answers.extraStates === 'yes' && filled(answers.extraStateList)) {
    stay.push(step(language, {
      id: 'foreign-qualification',
      title: ['Register in the other places', 'Regístrese en los otros lugares'],
      action: [`Register to operate in ${answers.extraStateList}. Each place wants its own application, fee, and tax account. A U.S. state calls this foreign qualification. Forming somewhere else does not skip the state where the work happens. If Texas is one of those places, file the foreign-entity application with the Secretary of State (the published fee for a foreign LLC is $750) and open the Comptroller franchise account.`, `Regístrese para operar en ${answers.extraStateList}. Cada lugar pide su propia solicitud, tarifa y cuenta fiscal. Un estado de EE.UU. llama a esto calificación foránea. Formarse en otro lugar no omite el estado donde ocurre el trabajo. Si Texas es uno de esos lugares, presente la solicitud de entidad foránea ante el Secretario de Estado (la tarifa publicada para una LLC foránea es $750) y abra la cuenta de franquicia del Contralor.`],
      hint: ['Approval from each extra place', 'Aprobación de cada lugar adicional'],
      priority: 'blocking',
      dependsOn: ['formation-filing'],
      proof: ['Save each approval', 'Guarde cada aprobación'],
    }));
  }
  const brandAndCustomers: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'domain-name',
      title: ['Register the company domain name', 'Registre el dominio de la empresa'],
      action: [`Buy the matching .com for ${answers.legalName} before you print cards or launch ads.`, `Compre el .com que coincida con ${answers.legalName} antes de imprimir tarjetas o lanzar anuncios.`],
      hint: ['Registrar receipt for the domain', 'Recibo del registrador del dominio'],
      priority: 'required',
      dependsOn: ['formation-filing'],
      sourceLabel: ['SBA — market your business', 'SBA — comercialice su negocio'],
      sourceUrl: SBA_MARKET,
      proof: ['Save the domain receipt and the renewal date', 'Guarde el recibo del dominio y la fecha de renovación'],
    }),
    ...companyEmailItems(language, 'domain-name'),
    step(language, {
      id: 'company-website',
      title: ['Publish a simple company website', 'Publique un sitio web simple de la empresa'],
      action: ['Put a live page on the domain with what the company does, how to contact you, and HTTPS.', 'Ponga una página en vivo en el dominio con lo que hace la empresa, cómo contactarlo y HTTPS.'],
      hint: ['Live URL on the company domain', 'URL en vivo en el dominio de la empresa'],
      priority: 'required',
      dependsOn: ['domain-name'],
      sourceLabel: ['SBA — market your business', 'SBA — comercialice su negocio'],
      sourceUrl: SBA_MARKET,
      proof: ['Save the live URL and a screenshot of the homepage', 'Guarde la URL en vivo y una captura de la página de inicio'],
    }),
    step(language, {
      id: 'logo-and-cards',
      title: ['Make a logo and business cards', 'Haga un logo y tarjetas de presentación'],
      action: ['Create a simple logo and print cards with the name, phone, company email, and website.', 'Cree un logo simple e imprima tarjetas con el nombre, teléfono, correo de la empresa y sitio web.'],
      hint: ['Logo file and card proof', 'Archivo del logo y prueba de las tarjetas'],
      priority: 'recommended',
      dependsOn: ['business-email-check', 'company-website'],
      sourceLabel: ['SBA — market your business', 'SBA — comercialice su negocio'],
      sourceUrl: SBA_MARKET,
      proof: ['Save the logo file and a photo or PDF of the card', 'Guarde el archivo del logo y una foto o PDF de la tarjeta'],
    }),
    step(language, {
      id: 'marketing-launch',
      title: ['Write the first marketing plan and outreach list', 'Escriba el primer plan de marketing y la lista de contactos'],
      action: ['Write who the first customers are, the offer in one sentence, and 10 to 20 people or companies to contact in the first two weeks.', 'Escriba quiénes son los primeros clientes, la oferta en una frase y 10 a 20 personas o empresas para contactar en las primeras dos semanas.'],
      hint: ['One-page plan plus outreach list', 'Plan de una página y lista de contactos'],
      priority: 'recommended',
      dependsOn: ['company-website', 'business-email-check'],
      sourceLabel: ['SBA — market your business', 'SBA — comercialice su negocio'],
      sourceUrl: SBA_MARKET,
      proof: ['Save the plan and the dated outreach list', 'Guarde el plan y la lista de contactos con fecha'],
    }),
  ];
  return [
    {
      id: 'formation',
      label: tx(language, 'Form the company', 'Formar la empresa'),
      summary: tx(language, `Name, local registry, and tax number in ${country}.`, `Nombre, registro local y número fiscal en ${country}.`),
      items: formation,
    },
    {
      id: 'tax-and-banking',
      label: tx(language, 'Bank and other places', 'Banco y otros lugares'),
      summary: tx(language, 'The company bank account, and registration anywhere else it will operate.', 'La cuenta bancaria de la empresa y el registro en cualquier otro lugar donde operará.'),
      items: stay,
    },
    {
      id: 'brand-and-customers',
      label: tx(language, 'Brand and customers', 'Marca y clientes'),
      summary: tx(
        language,
        'Domain, company email, website, cards, and the first outreach plan.',
        'Dominio, correo de la empresa, sitio web, tarjetas y el primer plan de contacto.',
      ),
      items: brandAndCustomers,
    },
  ];
};

const formationChecklistReady = (answers: BusinessCreationAnswers): boolean => (
  Boolean(answers.entity)
  && filled(answers.legalName)
  && (
    Boolean(answers.state && STATE_NAME.has(answers.state))
    || Boolean(answers.homeCountry && FORMATION_COUNTRY_CODES.includes(answers.homeCountry))
  )
);

export const businessCreationPillars = (
  answers: BusinessCreationAnswers,
  language: FormationLanguage,
): CompliancePillarDef[] => {
  // A later question, such as what the company offers, must not hide steps
  // already determined by the name, the state, and the kind of company.
  if (!formationChecklistReady(answers)) {
    return [];
  }
  if (answers.origin === 'foreign' && answers.homeCountry) {
    return foreignCountryPillars(answers, language);
  }
  const stateName = formationStateName(answers.state);
  const sole = answers.entity === 'sole-prop';
  const foreign = answers.origin === 'foreign';
  const pending = answers.stage === 'pending';
  const approved = answers.stage === 'approved';
  const einBlocking = answers.workers === 'employees'
    || answers.entity === 'corporation'
    || answers.entity === 'partnership'
    || answers.entity === 'nonprofit'
    || answers.owners === 'many'
    || foreign;

  const formation: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'name-availability',
      title: ['Confirm the name is available', 'Confirme que el nombre está disponible'],
      action: approved || pending
        ? [`Confirm ${answers.legalName} is the name on the ${stateName} filing, the bank, and the IRS letter.`, `Confirme que ${answers.legalName} es el nombre en la presentación de ${stateName}, el banco y la carta del IRS.`]
        : [`Search the ${stateName} business-name database for ${answers.legalName} before you file. A search does not reserve the name. The state still decides whether a similar name is too close when you submit the filing.`, `Busque ${answers.legalName} en el registro de nombres de ${stateName} antes de presentar. La búsqueda no reserva el nombre. El estado decide si un nombre parecido es demasiado cercano cuando usted presenta.`],
      hint: ['Name-search result', 'Resultado de la búsqueda del nombre'],
      priority: 'blocking',
      sourceLabel: nameSearchHowTo(answers.state).label,
      sourceUrl: nameSearchHowTo(answers.state).url,
      proof: ['Save the search result or the approved filing', 'Guarde el resultado de la búsqueda o la presentación aprobada'],
    }),
  ];

  if (!sole) {
    const filingTitle: Pair = foreign
      ? [`Register to do business in ${stateName}`, `Regístrese para operar en ${stateName}`]
      : answers.entity === 'partnership'
        ? [`Confirm the ${stateName} partnership filing`, `Confirme la presentación de la sociedad en ${stateName}`]
        : answers.entity === 'llc'
          ? [`File the ${stateName} LLC formation document`, `Presente el documento de formación de la LLC en ${stateName}`]
          : [`File the ${stateName} articles of incorporation`, `Presente los artículos de incorporación en ${stateName}`];
    const filingAction: Pair = pending
      ? [`The filing is already with ${stateName}. Keep the receipt and wait for acceptance before you open the bank account.`, `La presentación ya está en ${stateName}. Guarde el recibo y espere la aceptación antes de abrir la cuenta bancaria.`]
      : approved
        ? [`Save the stamped ${stateName} approval for ${answers.legalName}.`, `Guarde la aprobación sellada de ${stateName} para ${answers.legalName}.`]
        : foreign
          ? [`Form the company in its home country, then file the ${stateName} application for a foreign company to do business.`, `Forme la empresa en su país y luego presente en ${stateName} la solicitud de una empresa extranjera para operar.`]
          : answers.entity === 'partnership'
            ? [`Ask ${stateName} whether a general partnership, LP, or LLP must file a certificate. File it if the state requires it.`, `Pregunte a ${stateName} si una sociedad general, LP o LLP debe presentar un certificado. Preséntelo si el estado lo exige.`]
            : answers.entity === 'llc'
              ? answers.state === 'TX' && !foreign
                ? [`File Texas Form 205 for ${answers.legalName} through the Secretary of State portal. The published state fee is $300. ${answers.management === 'managers' ? 'Mark it manager-managed.' : 'Mark it member-managed.'} Put the registered agent’s Texas street address on the form. A name search does not reserve the name; the state decides whether a similar name is too close when you submit it.`, `Presente el Formulario 205 de Texas de ${answers.legalName} por el portal del Secretario de Estado. La tarifa estatal publicada es $300. ${answers.management === 'managers' ? 'Márquelo como gestionado por gerentes.' : 'Márquelo como gestionado por miembros.'} Ponga en el formulario el domicilio en Texas del agente registrado. La búsqueda del nombre no lo reserva; el estado decide si un nombre parecido es demasiado cercano cuando usted lo presenta.`]
                : [`File articles of organization (or the state’s certificate of formation) for ${answers.legalName}. ${answers.management === 'managers' ? 'Mark it manager-managed.' : 'Mark it member-managed.'}`, `Presente los artículos de organización (o el certificado de formación del estado) de ${answers.legalName}. ${answers.management === 'managers' ? 'Márquelo como gestionado por gerentes.' : 'Márquelo como gestionado por miembros.'}`]
              : [`File articles of incorporation for ${answers.legalName} with ${stateName}.`, `Presente los artículos de incorporación de ${answers.legalName} ante ${stateName}.`];
    formation.push(step(language, {
      id: 'formation-filing',
      title: filingTitle,
      action: filingAction,
      hint: ['Stamped approval or filing receipt', 'Aprobación sellada o recibo de presentación'],
      priority: 'blocking',
      dependsOn: ['name-availability'],
      sourceLabel: filingHowTo(answers.state, answers.entity).label,
      sourceUrl: filingHowTo(answers.state, answers.entity).url,
      docsLabel: answers.state === 'TX'
        ? ['Secretary of State — see if the filing was processed', 'Secretario de Estado — vea si la presentación ya se procesó']
        : undefined,
      docsUrl: answers.state === 'TX' ? TX_CERTIFICATE : undefined,
      waiting: pending
        ? [[`Wait for ${stateName} to accept the filing. Save the receipt in the file.`, `Espere a que ${stateName} acepte la presentación. Guarde el recibo en el expediente.`]]
        : undefined,
      proof: ['Save the stamped approval or the state receipt', 'Guarde la aprobación sellada o el recibo del estado'],
    }));
    formation.push(step(language, {
      id: 'registered-agent',
      title: ['Name the registered agent', 'Nombre el agente registrado'],
      action: answers.agent === 'service'
        ? [`Appoint a registered-agent service that will accept service for this company at a street address in ${stateName}. Get written consent, put that name and street address on the filing, and diary the renewal. Each company needs its own appointment, even when another company already uses the same service. A mailbox is not enough. The service must scan lawsuits and state notices the same day and tell you. Those official items must not count against a small regular-mail allowance. Junk mail can be discarded. Use an address you actually open for the IRS, the bank, and customers, unless the plan also scans that regular mail. A phone line and a meeting room are not required.`, `Designe un servicio de agente registrado que acepte notificaciones de esta empresa en un domicilio de ${stateName}. Obtenga el consentimiento por escrito, ponga ese nombre y domicilio en la presentación, y anote la renovación. Cada empresa necesita su propio nombramiento, aunque otra empresa ya use el mismo servicio. Un apartado postal no basta. El servicio debe escanear demandas y avisos del estado el mismo día y avisarle. Esos documentos oficiales no deben contar contra un cupo pequeño de correo ordinario. El correo basura se puede descartar. Use una dirección que usted sí abra para el IRS, el banco y los clientes, salvo que el plan también escanee ese correo ordinario. Una línea telefónica y una sala de reuniones no son necesarias.`]
        : answers.agent === 'owner'
          ? [`Name an owner with a street address in ${stateName} who can receive mail during business hours. A mailbox is not enough.`, `Nombre un dueño con domicilio en ${stateName} que pueda recibir correo en horario hábil. Un apartado postal no basta.`]
          : [`Choose a registered agent in ${stateName} before you file. The state will not accept the filing without one.`, `Elija un agente registrado en ${stateName} antes de presentar. El estado no acepta la presentación sin uno.`],
      hint: ['Agent name and street address', 'Nombre del agente y domicilio'],
      priority: 'blocking',
      dependsOn: ['name-availability'],
      sourceLabel: agentHowTo(answers.state).label,
      sourceUrl: agentHowTo(answers.state).url,
      proof: ['Save the agent acceptance or the address on the filing', 'Guarde la aceptación del agente o la dirección en la presentación'],
    }));
    const governing: Pair = answers.entity === 'llc'
      ? ['Sign an operating agreement', 'Firme un acuerdo operativo']
      : answers.entity === 'partnership'
        ? ['Sign a partnership agreement', 'Firme un contrato de sociedad']
        : ['Adopt bylaws', 'Adopte estatutos'];
    formation.push(step(language, {
      id: 'governing-document',
      title: governing,
      action: answers.entity === 'llc'
        ? [`Write the operating agreement even for one owner. It covers who decides, how money is split, and what happens if an owner leaves.${answers.owners === 'many' ? ' Write the real ownership split. Do not invent a percentage.' : ''}${answers.activity === 'online' ? ' Have each owner assign the software, the domain, and earlier work to the company in writing. A contractor who writes code must assign that work to the company before you pay them.' : ''} A government subscription does not require the company to be majority minority-owned. Do not assign 51 percent to chase a certificate. Texas VetHUB needs a service-disabled veteran who owns, manages, and runs at least 51 percent, with a disability rating of at least 20 percent. A federal set-aside that uses ownership also needs real control. Banks often ask for the agreement. Most states do not require you to file it.`, `Redacte el acuerdo operativo incluso con un solo dueño. Cubre quién decide, cómo se reparte el dinero y qué pasa si un dueño se va.${answers.owners === 'many' ? ' Escriba el reparto real de la propiedad. No invente un porcentaje.' : ''}${answers.activity === 'online' ? ' Cada dueño debe ceder por escrito a la empresa el software, el dominio y el trabajo anterior. Un contratista que escriba código debe ceder ese trabajo a la empresa antes de que usted le pague.' : ''} Una suscripción de una agencia de gobierno no exige que la empresa sea de mayoría minoritaria. No asigne 51 por ciento para perseguir un certificado. VetHUB de Texas exige un veterano con discapacidad de servicio que posea, gestione y dirija al menos el 51 por ciento, con una calificación de discapacidad de al menos 20 por ciento. Una reserva federal que usa la propiedad también exige control real. Los bancos suelen pedir el acuerdo. La mayoría de los estados no exigen presentarlo.`]
        : answers.entity === 'partnership'
          ? ['Write who owns what, who decides, and how a partner exits.', 'Escriba quién posee qué, quién decide y cómo sale un socio.']
          : ['Adopt bylaws and hold the first board action that names officers and approves the bank account.', 'Adopte estatutos y haga la primera acción del directorio que nombre oficiales y apruebe la cuenta bancaria.'],
      hint: ['Signed agreement or adopted bylaws', 'Acuerdo firmado o estatutos adoptados'],
      priority: 'required',
      dependsOn: ['formation-filing'],
      sourceLabel: ['SBA — choose a business structure', 'SBA — elija la estructura del negocio'],
      sourceUrl: SBA_STRUCTURE,
      proof: ['Save the signed agreement', 'Guarde el acuerdo firmado'],
    }));
  }

  if (answers.entity === 'llc' && answers.state && ['NY', 'AZ', 'NE'].includes(answers.state)) {
    const publication: Pair = answers.state === 'NY'
      ? ['Publish the New York LLC notice', 'Publique el aviso de la LLC de Nueva York']
      : [`Confirm the ${stateName} publication rule`, `Confirme la regla de publicación de ${stateName}`];
    formation.push(step(language, {
      id: 'publication',
      title: publication,
      action: answers.state === 'NY'
        ? ['New York requires an LLC to publish a notice and file proof. Confirm the newspapers and the 120-day proof deadline with the Department of State. Missing it can suspend the LLC’s authority to do business.', 'Nueva York exige que una LLC publique un aviso y presente la prueba. Confirme los periódicos y el plazo de 120 días para la prueba con el Department of State. Omitirlo puede suspender la autoridad de la LLC para operar.']
        : [`${stateName} has required LLC publication. Confirm the current notice rule with the state before you treat formation as finished.`, `${stateName} ha exigido publicación de la LLC. Confirme la regla vigente del aviso con el estado antes de dar por terminada la formación.`],
      hint: ['Publication affidavit or state proof', 'Declaración de publicación o prueba del estado'],
      priority: 'blocking',
      dependsOn: ['formation-filing'],
      sourceLabel: answers.state === 'NY'
        ? ['New York Department of State — LLC', 'Departamento de Estado de Nueva York — LLC']
        : ['SBA — register your business', 'SBA — registre su negocio'],
      sourceUrl: answers.state === 'NY' ? 'https://dos.ny.gov/limited-liability-company' : SBA_REGISTER,
      proof: ['Save the publication proof', 'Guarde la prueba de publicación'],
    }));
  }

  if (!sole && answers.state && ['CA', 'NV', 'LA'].includes(answers.state)) {
    formation.push(step(language, {
      id: 'initial-report',
      title: [`File the ${stateName} first report`, `Presente el primer reporte de ${stateName}`],
      action: answers.state === 'CA'
        ? ['California requires a Statement of Information soon after formation, often within 90 days. Confirm the current window and file it.', 'California exige una Statement of Information poco después de la formación, a menudo en 90 días. Confirme el plazo vigente y preséntela.']
        : answers.state === 'NV'
          ? ['Nevada requires an initial list of members or managers. Confirm whether it went in with the articles or is still due.', 'Nevada exige una lista inicial de miembros o gerentes. Confirme si fue con los artículos o si todavía está pendiente.']
          : ['Louisiana requires an initial report. Confirm the deadline with the Secretary of State before you treat formation as finished.', 'Luisiana exige un reporte inicial. Confirme el plazo con el Secretario de Estado antes de dar por terminada la formación.'],
      hint: ['Filed initial report or statement', 'Reporte inicial o declaración presentada'],
      priority: 'required',
      dependsOn: ['formation-filing'],
      sourceUrl: answers.state === 'CA' ? 'https://www.sos.ca.gov/business-programs/business-entities/statements' : SBA_REGISTER,
      sourceLabel: ['State business filing office', 'Oficina estatal de presentaciones'],
      proof: ['Save the filed report', 'Guarde el reporte presentado'],
    }));
  }

  if (answers.useDba === 'yes') {
    formation.push(step(language, {
      id: 'assumed-name',
      title: ['File the trade name', 'Presente el nombre comercial'],
      action: [`File ${answers.dbaName} as a DBA or assumed name in ${stateName} (and the county, if that is who takes the filing) before you use it on a bank account.`, `Presente ${answers.dbaName} como DBA o nombre comercial en ${stateName} (y en el condado, si es quien recibe la presentación) antes de usarlo en una cuenta bancaria.`],
      hint: ['Assumed-name certificate', 'Certificado de nombre comercial'],
      priority: sole ? 'blocking' : 'required',
      dependsOn: sole ? ['name-availability'] : ['formation-filing'],
      sourceLabel: answers.state === 'TX'
        ? ['Texas Secretary of State — assumed name forms', 'Secretario de Estado de Texas — formularios de nombre comercial']
        : SBA_REGISTER_HOW.label,
      sourceUrl: answers.state === 'TX' ? TX_FORMS : SBA_REGISTER,
      proof: ['Save the assumed-name certificate', 'Guarde el certificado de nombre comercial'],
    }));
  }

  if (!sole) {
    formation.push(step(language, {
      id: 'periodic-report',
      title: [`Calendar the ${stateName} recurring report`, `Ponga en el calendario el reporte periódico de ${stateName}`],
      action: answers.state === 'TX'
        ? ['After the state accepts the filing, open a Texas Comptroller account. Put the franchise report and the Public Information Report on a calendar, due May 15 (or the next business day when that date is a weekend or holiday). A company formed this year files the first report the following May 15. The Comptroller’s 2026 no-tax-due threshold is $2.65 million, so the tax can be zero, but the Public Information Report is still required. The separate No Tax Due Report is no longer filed. Confirm the threshold for the year you file. Name an owner on the calendar. Missing the report can cost the company the right to do business in Texas.', 'Cuando el estado acepte la presentación, abra una cuenta en el Contralor de Texas. Ponga en el calendario el reporte de franquicia y el Reporte de Información Pública, con vencimiento el 15 de mayo (o el siguiente día hábil si esa fecha cae en fin de semana o feriado). Una empresa formada este año presenta el primer reporte el 15 de mayo siguiente. El umbral de 2026 del Contralor para no deber impuesto es $2.65 millones, así que el impuesto puede ser cero, pero el Reporte de Información Pública sigue siendo obligatorio. Ya no se presenta el reporte separado de impuesto no debido. Confirme el umbral del año en que presente. Nombre un dueño en el calendario. Omitir el reporte puede costarle a la empresa el derecho de operar en Texas.']
        : [`Put the ${stateName} annual, biennial, or franchise report on a calendar with an owner and the fee. Missing it can mean late fees or administrative dissolution.`, `Anote el reporte anual, bienal o de franquicia de ${stateName} en un calendario, con un dueño y la tarifa. Omitirlo puede significar multas o disolución administrativa.`],
      hint: ['Deadline and owner name', 'Plazo y nombre del responsable'],
      priority: 'required',
      dependsOn: ['formation-filing'],
      sourceLabel: reportHowTo(answers.state).label,
      sourceUrl: reportHowTo(answers.state).url,
      docsLabel: answers.state === 'TX'
        ? ['Comptroller — see if this company is on file', 'Contralor — vea si esta empresa ya está registrada']
        : undefined,
      docsUrl: answers.state === 'TX' ? 'https://mycpa.cpa.state.tx.us/coa/' : undefined,
      proof: ['Save the calendar entry and the owner’s name', 'Guarde la entrada del calendario y el nombre del responsable'],
    }));
  }

  const taxItems: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'ein',
      title: ['Get an EIN', 'Obtenga un EIN'],
      action: einBlocking
        ? ['Apply for an EIN with the IRS. Employees, corporations, partnerships, nonprofits, and multi-owner LLCs need one. The responsible party uses their own Social Security number on the application. Use the EIN on the bank account and payroll. Do not invent the number.', 'Solicite un EIN al IRS. Lo necesitan los empleados, las corporaciones, las sociedades, las organizaciones sin fines de lucro y las LLC con varios dueños. La persona responsable usa su propio número de Seguro Social en la solicitud. Use el EIN en la cuenta bancaria y la nómina. No invente el número.']
        : ['Apply for an EIN if you will open a bank account in the business name. A sole owner with no employees can sometimes use a Social Security number, but the bank often still asks for an EIN.', 'Solicite un EIN si abrirá una cuenta a nombre del negocio. Un solo dueño sin empleados a veces puede usar el número de Seguro Social, pero el banco igual suele pedir un EIN.'],
      hint: ['IRS CP575 or EIN confirmation', 'CP575 del IRS o confirmación del EIN'],
      priority: einBlocking ? 'blocking' : 'recommended',
      dependsOn: sole ? ['name-availability'] : ['formation-filing'],
      sourceLabel: ['IRS — get an EIN', 'IRS — obtenga un EIN'],
      sourceUrl: IRS_EIN,
      proof: ['Save the EIN confirmation. Do not type the full number into notes if you can store the letter instead.', 'Guarde la confirmación del EIN. No escriba el número completo en las notas si puede guardar la carta.'],
    }),
  ];

  if (answers.tax === 's-corp' || (answers.entity === 'llc' && answers.tax === 'c-corp')) {
    taxItems.push(step(language, {
      id: 'tax-election',
      title: answers.tax === 's-corp'
        ? ['File the S corporation election', 'Presente la elección de corporación S']
        : ['File the C corporation election', 'Presente la elección de corporación C'],
      action: answers.tax === 's-corp'
        ? [`File IRS Form 2553 after the EIN arrives.${answers.owners === 'many' ? ' Every owner signs.' : ''} The usual deadline is two months and 15 days after the election start date. Confirm that deadline and the tax year on the form before you send it. Once the company has profit, working owners pay themselves reasonable wages.`, `Presente el Formulario 2553 del IRS cuando llegue el EIN.${answers.owners === 'many' ? ' Cada dueño firma.' : ''} El plazo habitual es dos meses y 15 días después de la fecha de inicio de la elección. Confirme ese plazo y el año fiscal en el formulario antes de enviarlo. Cuando la empresa tenga ganancias, los dueños que trabajan se pagan un salario razonable.`]
        : ['File IRS Form 8832 so the LLC is taxed as a C corporation. Profit then stays on the company return instead of flowing onto the owners’ personal returns. Confirm the effective date on the form before you send it. This election does not replace franchise tax in the state where the company does business.', 'Presente el Formulario 8832 del IRS para que la LLC tribute como corporación C. La ganancia queda entonces en la declaración de la empresa en vez de pasar a las declaraciones personales de los dueños. Confirme la fecha efectiva en el formulario antes de enviarlo. Esta elección no sustituye el impuesto de franquicia del estado donde opera la empresa.'],
      hint: answers.tax === 's-corp'
        ? ['Filed Form 2553 or IRS acceptance', 'Formulario 2553 presentado o aceptación del IRS']
        : ['Filed Form 8832 or IRS acceptance', 'Formulario 8832 presentado o aceptación del IRS'],
      priority: 'blocking',
      dependsOn: ['ein'],
      sourceLabel: ['IRS — Form 2553', 'IRS — Formulario 2553'],
      sourceUrl: IRS_2553,
      proof: ['Save the IRS acceptance', 'Guarde la aceptación del IRS'],
    }));
  }

  if (answers.entity === 'nonprofit' && answers.exempt === 'apply') {
    taxItems.push(step(language, {
      id: 'charity-exemption',
      title: ['Apply for 501(c)(3) status', 'Solicite el estatus 501(c)(3)'],
      action: ['After the EIN arrives, apply for IRS recognition of exemption. State formation alone does not make the nonprofit tax-exempt.', 'Cuando llegue el EIN, solicite al IRS el reconocimiento de la exención. La formación estatal por sí sola no hace exenta a la organización.'],
      hint: ['IRS determination letter', 'Carta de determinación del IRS'],
      priority: 'required',
      dependsOn: ['ein'],
      sourceLabel: ['IRS — apply for exemption', 'IRS — solicite la exención'],
      sourceUrl: IRS_1023,
      proof: ['Save the determination letter', 'Guarde la carta de determinación'],
    }));
  }

  if (answers.entity === 'nonprofit') {
    taxItems.push(step(language, {
      id: 'nonprofit-conflict-policy',
      title: ['Adopt a conflict-of-interest policy', 'Adopte una política de conflicto de interés'],
      action: ['Have the board adopt a written policy. Each director signs it. The IRS exemption application asks whether you have one.', 'El directorio adopta una política por escrito. Cada director la firma. La solicitud de exención del IRS pregunta si la tienen.'],
      hint: ['Signed policy', 'Política firmada'],
      priority: 'required',
      dependsOn: ['governing-document'],
      sourceLabel: ['IRS — governance and related topics', 'IRS — gobierno y temas relacionados'],
      sourceUrl: 'https://www.irs.gov/charities-non-profits/governance-and-related-topics',
      proof: ['Save the signed policy', 'Guarde la política firmada'],
    }));
    taxItems.push(step(language, {
      id: 'nonprofit-annual-return',
      title: ['Calendar the annual information return', 'Ponga en el calendario la declaración anual de información'],
      action: ['Most tax-exempt organizations file Form 990, the short form, or the e-postcard. It is due the 15th day of the fifth month after the year ends. Missing it three years in a row can end the exemption. Confirm which form fits this year’s receipts.', 'La mayoría de las organizaciones exentas presentan el Form 990, el formulario corto o la postal electrónica. Vence el día 15 del quinto mes después de que termina el año. Omitirlo tres años seguidos puede terminar la exención. Confirme qué formulario corresponde a los ingresos de este año.'],
      hint: ['Filed return or e-postcard acceptance', 'Declaración presentada o aceptación de la postal'],
      priority: 'required',
      dependsOn: ['ein'],
      sourceLabel: ['IRS — Form 990 instructions', 'IRS — instrucciones del Form 990'],
      sourceUrl: 'https://www.irs.gov/instructions/i990',
      proof: ['Save the acceptance', 'Guarde la aceptación'],
    }));
    taxItems.push(step(language, {
      id: 'nonprofit-solicitation',
      title: ['Register before asking the public for money', 'Regístrese antes de pedir dinero al público'],
      action: [`Most states require a charity to register before it asks a resident for a donation, then renew every year. Start with ${stateName}. If you will ask people in other states, register there too. Confirm the office, the fee, and any exemption before the first ask.`, `La mayoría de los estados exigen que una caridad se registre antes de pedir una donación a un residente, y que renueve cada año. Empiece por ${stateName}. Si pedirá en otros estados, regístrese también allí. Confirme la oficina, la tarifa y cualquier exención antes del primer pedido.`],
      hint: ['State charity registration', 'Registro estatal de caridad'],
      priority: 'required',
      dependsOn: ['ein'],
      sourceLabel: ['National Council of Nonprofits — state filing requirements', 'National Council of Nonprofits — requisitos estatales'],
      sourceUrl: 'https://www.councilofnonprofits.org/running-nonprofit/governance-leadership/state-filing-requirements-nonprofits',
      proof: ['Save the registration and the renewal date', 'Guarde el registro y la fecha de renovación'],
    }));
  }

  taxItems.push(step(language, {
    id: 'business-bank',
    title: answers.bank === 'yes'
      ? ['Keep the business bank account in the legal name', 'Mantenga la cuenta bancaria a nombre legal']
      : ['Open a business bank account', 'Abra una cuenta bancaria del negocio'],
    action: answers.bank === 'yes'
      ? [`Confirm the account is titled ${answers.legalName}, not a personal name. Keep business and personal money separate. The bank will still ask who owns and controls the company. Bring the operating agreement. That request is separate from a FinCEN BOI report.`, `Confirme que la cuenta está a nombre de ${answers.legalName}, no de una persona. Separe el dinero del negocio y el personal. El banco igual preguntará quién posee y controla la empresa. Lleve el acuerdo operativo. Esa pregunta es aparte de un reporte BOI de FinCEN.`]
      : [`Open an account in the name ${answers.legalName} after the state approval and the EIN are in hand. Keep business and personal money separate. The bank will still ask who owns and controls the company. Bring the operating agreement. That request is separate from a FinCEN BOI report.`, `Abra una cuenta a nombre de ${answers.legalName} cuando tenga la aprobación del estado y el EIN. Separe el dinero del negocio y el personal. El banco igual preguntará quién posee y controla la empresa. Lleve el acuerdo operativo. Esa pregunta es aparte de un reporte BOI de FinCEN.`],
    hint: ['Bank letter or voided check in the legal name', 'Carta del banco o cheque anulado a nombre legal'],
    priority: answers.bank === 'yes' ? 'required' : 'blocking',
    dependsOn: ['ein'],
    sourceLabel: ['SBA — open a business bank account', 'SBA — abra una cuenta bancaria del negocio'],
    sourceUrl: SBA_BANK,
    proof: ['Save a bank letter. Do not store the full account number here.', 'Guarde una carta del banco. No guarde aquí el número completo de la cuenta.'],
  }));

  if (foreign) {
    taxItems.push(step(language, {
      id: 'boi-file',
      title: ['File the foreign-company BOI report', 'Presente el reporte BOI de la empresa extranjera'],
      action: ['A company created outside the United States and registered to do business in a U.S. state may still have to file beneficial-ownership information with FinCEN. Report non-U.S. owners. Do not report U.S. person owners. Confirm the 30-day window on FinCEN’s site.', 'Una empresa creada fuera de Estados Unidos y registrada para operar en un estado puede tener que presentar información de titularidad real ante FinCEN. Reporte a los dueños que no son de EE.UU. No reporte a las personas de EE.UU. Confirme el plazo de 30 días en el sitio de FinCEN.'],
      hint: ['FinCEN filing acceptance', 'Aceptación de la presentación ante FinCEN'],
      priority: 'blocking',
      dependsOn: ['formation-filing'],
      sourceLabel: ['FinCEN — beneficial ownership', 'FinCEN — titularidad real'],
      sourceUrl: FINCEN_BOI,
      proof: ['Save the FinCEN acceptance. Do not paste owner ID numbers into NextCompli.', 'Guarde la aceptación de FinCEN. No pegue números de identidad de los dueños en NextCompli.'],
    }));
  } else if (!sole) {
    taxItems.push(step(language, {
      id: 'boi-exempt',
      title: ['Record the BOI exemption', 'Registre la exención de BOI'],
      action: ['Companies formed in the United States do not file a beneficial-ownership report under the August 2026 FinCEN rule. Record that this company is exempt. Do not file a BOI report for it.', 'Las empresas formadas en Estados Unidos no presentan un reporte de titularidad real bajo la regla de FinCEN de agosto de 2026. Registre que esta empresa está exenta. No presente un reporte BOI por ella.'],
      hint: ['Note that the company is U.S.-formed and exempt', 'Nota de que la empresa se formó en EE.UU. y está exenta'],
      priority: 'recommended',
      sourceLabel: ['FinCEN — beneficial ownership', 'FinCEN — titularidad real'],
      sourceUrl: FINCEN_BOI,
      proof: ['Save a note with the date you checked FinCEN', 'Guarde una nota con la fecha en que consultó FinCEN'],
    }));
  }

  const licenseItems: ComplianceChecklistItemDef[] = [];
  const activityCopy: Record<string, { title: Pair; action: Pair; priority: ComplianceItemPriority }> = {
    professional: {
      title: ['Get the professional license', 'Obtenga la licencia profesional'],
      action: [`Confirm the ${stateName} license for this profession before you offer the service. The state business filing is not a professional license.`, `Confirme la licencia de ${stateName} para esta profesión antes de ofrecer el servicio. La presentación empresarial del estado no es una licencia profesional.`],
      priority: 'blocking',
    },
    food: {
      title: ['Get the food, alcohol, or lodging permits', 'Obtenga los permisos de comida, alcohol u hospedaje'],
      action: ['Get the local health permit. If you will serve or sell alcohol, get that license separately before you open.', 'Obtenga el permiso local de salud. Si va a servir o vender alcohol, obtenga esa licencia por separado antes de abrir.'],
      priority: 'blocking',
    },
    goods: {
      title: ['List the city and state business licenses', 'Liste las licencias municipales y estatales'],
      action: [`Ask the city and ${stateName} which general business licenses apply before you sell.`, `Pregunte a la ciudad y a ${stateName} qué licencias generales aplican antes de vender.`],
      priority: 'required',
    },
    field: {
      title: ['Confirm contractor or transportation permits', 'Confirme permisos de contratista o transporte'],
      action: [`Confirm whether ${stateName} requires contractor registration or a transportation permit for this work.`, `Confirme si ${stateName} exige registro de contratista o un permiso de transporte para este trabajo.`],
      priority: 'required',
    },
    online: {
      title: ['Check the home-occupation rule', 'Revise la regla de oficio en casa'],
      action: ['Name the city where the work happens, then ask that city whether a home-based or online service needs a local license or a zoning OK. Do not guess the city.', 'Nombre la ciudad donde se hace el trabajo y luego pregunte a esa ciudad si un servicio en casa o en línea necesita licencia local o un visto bueno de zonificación. No adivine la ciudad.'],
      priority: 'recommended',
    },
    other: {
      title: ['List the licenses for this activity', 'Liste las licencias de esta actividad'],
      action: ['Write the federal, state, and city licenses this activity needs. Formation does not include them automatically.', 'Escriba las licencias federales, estatales y municipales que esta actividad necesita. La formación no las incluye solas.'],
      priority: 'required',
    },
  };
  const activity = activityCopy[answers.activity || 'other'];
  licenseItems.push(step(language, {
    id: 'activity-license',
    title: activity.title,
    action: activity.action,
    hint: ['License number or the agency’s “not required” note', 'Número de licencia o nota de la agencia de que no se exige'],
    priority: activity.priority,
    dependsOn: sole ? ['name-availability'] : ['formation-filing'],
    sourceLabel: ['SBA — licenses and permits', 'SBA — licencias y permisos'],
    sourceUrl: SBA_LICENSES,
    proof: ['Save the license or the agency note', 'Guarde la licencia o la nota de la agencia'],
  }));

  if (answers.salesTax === 'yes' || answers.salesTax === 'unsure') {
    const texasSoftware = answers.state === 'TX' && answers.activity === 'online';
    licenseItems.push(step(language, {
      id: 'sales-tax-permit',
      title: answers.salesTax === 'yes' || texasSoftware
        ? ['Get a seller’s permit', 'Obtenga un permiso de vendedor']
        : ['Confirm whether sales tax applies', 'Confirme si aplica el impuesto sobre ventas'],
      action: texasSoftware
        ? ['Texas taxes hosted software and software subscriptions as data processing. Twenty percent of the charge is exempt and eighty percent is taxable under 34 Texas Administrative Code § 3.330. Get a Texas sales tax permit before the first taxable sale. Forming the company in another state does not remove this tax when the company is run from Texas or sells to Texas customers.', 'Texas grava el software alojado y las suscripciones de software como procesamiento de datos. El veinte por ciento del cargo está exento y el ochenta por ciento está gravado según el Código Administrativo de Texas, título 34, § 3.330. Obtenga un permiso de impuesto sobre ventas de Texas antes de la primera venta gravada. Formar la empresa en otro estado no quita este impuesto si la empresa se opera desde Texas o vende a clientes de Texas.']
        : answers.salesTax === 'yes'
        ? [`Get a ${stateName} seller’s permit before the first taxable sale.`, `Obtenga un permiso de vendedor de ${stateName} antes de la primera venta gravada.`]
        : [`Ask the ${stateName} tax office whether these sales are taxable before you start selling.`, `Pregunte a la oficina fiscal de ${stateName} si estas ventas están gravadas antes de empezar a vender.`],
      hint: ['Permit or written “not taxable” answer', 'Permiso o respuesta escrita de que no está gravado'],
      guides: answers.state === 'TX'
        ? [
          ['Open https://comptroller.texas.gov/taxes/permit/', 'Abra https://comptroller.texas.gov/taxes/permit/'],
          ['Click the words “Apply for Permit via eSystems”.', 'Pulse las palabras “Apply for Permit via eSystems”.'],
          ['Have these ready: the Texas Secretary of State file number, the EIN, and the NAICS code. Do not type a Social Security number into NextCompli.', 'Tenga listos el número de archivo del Secretario de Estado de Texas, el EIN y el código NAICS. No escriba un número de Seguro Social en NextCompli.'],
          ['When it asks which tax, choose Sales and Use Tax.', 'Cuando pregunte qué impuesto, elija Sales and Use Tax.'],
          ['When it asks what the company sells, write software or software subscriptions. Texas calls that data processing.', 'Cuando pregunte qué vende la empresa, escriba software o suscripciones de software. Texas llama a eso procesamiento de datos.'],
          ['On the last page, print the page named Online Tax Registration Summary.', 'En la última página, imprima la página llamada Online Tax Registration Summary.'],
          ['Wait for the permit in the mail. The Comptroller says that takes about 2 to 3 weeks. There is no fee.', 'Espere el permiso por correo. El Contralor dice que tarda unas 2 a 3 semanas. No hay cuota.'],
          ['When the permit arrives, save a photo or a PDF. Look for the LLC legal name on it.', 'Cuando llegue el permiso, guarde una foto o un PDF. Busque el nombre legal de la LLC.'],
        ]
        : [
          [`Open the ${stateName} tax office page from the link on this step.`, `Abra la página de la oficina fiscal de ${stateName} desde el enlace de este paso.`],
          ['Search that site for “sales tax permit” or “seller’s permit”.', 'Busque en ese sitio “sales tax permit” o “seller’s permit”.'],
          ['Apply in the LLC legal name before the first taxable sale. Do not type a Social Security number into NextCompli.', 'Solicite a nombre legal de la LLC antes de la primera venta gravada. No escriba un número de Seguro Social en NextCompli.'],
          ['Save the permit, or the tax office’s written note that the sales are not taxable.', 'Guarde el permiso, o la nota escrita de la oficina fiscal de que las ventas no están gravadas.'],
        ],
      priority: answers.salesTax === 'yes' || texasSoftware ? 'blocking' : 'required',
      dependsOn: sole ? ['name-availability'] : ['formation-filing'],
      sourceLabel: salesTaxHowTo(answers.state).label,
      sourceUrl: salesTaxHowTo(answers.state).url,
      proof: ['Save the permit or the tax office’s answer', 'Guarde el permiso o la respuesta de la oficina fiscal'],
    }));
  }

  if (answers.activity === 'online') {
    const texasCloud = answers.state === 'TX';
    licenseItems.push(step(language, {
      id: 'government-cloud-bar',
      title: ['Set the government security bar', 'Fije el nivel de seguridad para el gobierno'],
      action: texasCloud
        ? ['If a government agency will store its records in this online service, use the normal security bar. Texas state agencies, public universities, and community colleges require TX-RAMP Level 2 for confidential data. Federal agencies require FedRAMP Moderate for that same kind of data. TX-RAMP Level 1 and FedRAMP Low only cover public or nonconfidential data. A city or school district often asks for a SOC 2 report instead. Start the certification when the product is running and a buyer is ready. Texas can issue provisional TX-RAMP status for 18 months during an active purchase.', 'Si una agencia de gobierno guardará sus registros en este servicio en línea, use el nivel de seguridad normal. Las agencias estatales de Texas, las universidades públicas y los colegios comunitarios exigen TX-RAMP nivel 2 para datos confidenciales. Las agencias federales exigen FedRAMP Moderate para ese mismo tipo de datos. TX-RAMP nivel 1 y FedRAMP Low solo cubren datos públicos o no confidenciales. Una ciudad o un distrito escolar suele pedir un informe SOC 2. Empiece la certificación cuando el producto esté en marcha y haya un comprador listo. Texas puede dar un estatus provisional de TX-RAMP por 18 meses durante una compra activa.']
        : ['If a government agency will store its records in this online service, the normal federal bar is FedRAMP Moderate. FedRAMP Low only covers public or nonconfidential data. Ask the state whether it has its own cloud-security certificate. A city often asks for a SOC 2 report. Start the certification when the product is running and a buyer is ready.', 'Si una agencia de gobierno guardará sus registros en este servicio en línea, el nivel federal normal es FedRAMP Moderate. FedRAMP Low solo cubre datos públicos o no confidenciales. Pregunte al estado si tiene su propio certificado de seguridad en la nube. Una ciudad suele pedir un informe SOC 2. Empiece la certificación cuando el producto esté en marcha y haya un comprador listo.'],
      hint: ['Required level, or a note that no agency will store records here', 'Nivel exigido, o una nota de que ninguna agencia guardará registros aquí'],
      priority: 'recommended',
      sourceLabel: texasCloud
        ? ['Texas — cloud security levels for agency subscriptions', 'Texas — niveles de seguridad en la nube para suscripciones de agencias']
        : ['SBA — licenses and permits', 'SBA — licencias y permisos'],
      sourceUrl: texasCloud
        ? 'https://dir.texas.gov/information-security/texas-risk-and-authorization-management-program-tx-ramp/tx-ramp-eligibility'
        : SBA_LICENSES,
      proof: ['Save the buyer’s required level, or a note that government agencies will not store records in the service', 'Guarde el nivel que exige el comprador, o una nota de que las agencias de gobierno no guardarán registros en el servicio'],
    }));
  }

  if (answers.activity === 'online' && answers.extraStates !== 'yes') {
    licenseItems.push(step(language, {
      id: 'customer-state-tax',
      title: ['Check sales tax where customers are', 'Revise el impuesto sobre ventas donde están los clientes'],
      action: ['An office in another state is not the only way that state can tax a software sale. Before sales to customers outside the home state add up, check that state’s rule for remote sellers. Do not assume one national dollar amount. A government customer is not automatically exempt.', 'Una oficina en otro estado no es la única forma en que ese estado puede gravar una venta de software. Antes de que se acumulen ventas a clientes fuera del estado de origen, revise la regla de ese estado para vendedores remotos. No suponga una cifra nacional única. Un cliente del gobierno no está exento automáticamente.'],
      hint: ['Note of the states you checked', 'Nota de los estados que revisó'],
      priority: 'recommended',
      sourceLabel: answers.state === 'TX'
        ? ['Texas Comptroller — remote sellers', 'Contralor de Texas — vendedores remotos']
        : SBA_LICENSE_HOW.label,
      sourceUrl: answers.state === 'TX'
        ? 'https://comptroller.texas.gov/taxes/sales/remote-sellers.php'
        : SBA_LICENSES,
      proof: ['Save the states you checked and whether a permit was required', 'Guarde los estados que revisó y si exigían un permiso'],
    }));
  }

  const people: ComplianceChecklistItemDef[] = [];
  if (answers.workers === 'employees') {
    people.push(step(language, {
      id: 'payroll-accounts',
      title: ['Open state payroll accounts', 'Abra las cuentas estatales de nómina'],
      action: [`After the EIN, register for ${stateName} withholding and unemployment before the first paycheck.`, `Después del EIN, regístrese para la retención y el desempleo de ${stateName} antes del primer pago.`],
      hint: ['State payroll account numbers', 'Números de cuenta de nómina del estado'],
      priority: 'blocking',
      dependsOn: ['ein'],
      sourceLabel: payrollHowTo(answers.state).label,
      sourceUrl: payrollHowTo(answers.state).url,
      proof: ['Save the registration letters', 'Guarde las cartas de registro'],
    }));
    people.push(step(language, {
      id: 'workers-comp',
      title: ['Get workers’ compensation', 'Obtenga la compensación laboral'],
      action: answers.state === 'TX'
        ? ['Before the first employee starts, decide on workers’ compensation. Texas does not require it for most private employers, but a contract can. Health insurance is not a substitute. Confirm whether owners must be covered too.', 'Antes de que empiece el primer empleado, decida la compensación laboral. Texas no la exige a la mayoría de los empleadores privados, pero un contrato puede exigirla. El seguro de salud no la sustituye. Confirme si los dueños también deben estar cubiertos.']
        : [`Get workers’ compensation before the first employee starts in ${stateName}. Confirm whether owners must be covered too.`, `Obtenga la compensación laboral antes de que empiece el primer empleado en ${stateName}. Confirme si los dueños también deben estar cubiertos.`],
      hint: ['Policy declaration or state exemption', 'Declaración de la póliza o exención del estado'],
      priority: answers.state === 'TX' ? 'required' : 'blocking',
      dependsOn: ['ein'],
      sourceLabel: workersCompHowTo(answers.state).label,
      sourceUrl: workersCompHowTo(answers.state).url,
      proof: ['Save the policy declaration. A COI is the one-page proof, not the whole policy.', 'Guarde la declaración de la póliza. Un COI es la prueba de una página, no la póliza entera.'],
    }));
  } else if (answers.workers === 'contractors') {
    people.push(step(language, {
      id: 'contractor-w9',
      title: ['Collect W-9s from contractors', 'Pida el W-9 a los contratistas'],
      action: ['Get a W-9 before you pay a U.S. contractor. A contractor outside the United States gives you IRS Form W-8BEN instead. Do not put them on payroll unless they are actually employees.', 'Pida un W-9 antes de pagar a un contratista de EE.UU. Un contratista fuera de Estados Unidos entrega el Formulario W-8BEN del IRS. No los ponga en nómina salvo que realmente sean empleados.'],
      hint: ['W-9 on file with the bookkeeper', 'W-9 en el expediente del contador'],
      priority: 'required',
      sourceLabel: ['IRS — Form W-9', 'IRS — Formulario W-9'],
      sourceUrl: IRS_W9,
      proof: ['Note where the W-9s are kept. Do not upload Social Security numbers here.', 'Anote dónde se guardan los W-9. No suba aquí números de Seguro Social.'],
    }));
  }

  const stay: ComplianceChecklistItemDef[] = [];
  if (answers.extraStates === 'yes') {
    stay.push(step(language, {
      id: 'foreign-qualification',
      title: ['Register in the other states', 'Regístrese en los otros estados'],
      action: [`Register to do business in ${answers.extraStateList}. Each state wants its own application, fee, registered agent, and tax account. Forming somewhere else does not skip the state where the work happens. If Texas is one of those places, file the foreign-entity application with the Secretary of State (the published fee for a foreign LLC is $750) and open the Comptroller franchise account. The franchise report is still due even when the tax is zero.`, `Regístrese para operar en ${answers.extraStateList}. Cada estado pide su propia solicitud, tarifa, agente registrado y cuenta fiscal. Formarse en otro lugar no omite el estado donde ocurre el trabajo. Si Texas es uno de esos lugares, presente la solicitud de entidad foránea ante el Secretario de Estado (la tarifa publicada para una LLC foránea es $750) y abra la cuenta de franquicia del Contralor. El reporte de franquicia sigue venciendo aunque el impuesto sea cero.`],
      hint: ['Approval from each extra state', 'Aprobación de cada estado adicional'],
      priority: 'blocking',
      dependsOn: sole ? ['name-availability'] : ['formation-filing'],
      sourceLabel: ['SBA — register your business', 'SBA — registre su negocio'],
      sourceUrl: SBA_REGISTER,
      proof: ['Save each state’s approval', 'Guarde la aprobación de cada estado'],
    }));
  }

  stay.push(step(language, {
    id: 'insurance-cgl',
    title: ['Decide on CGL insurance', 'Decida el seguro CGL'],
    action: ['Commercial general liability pays if someone is hurt or property is damaged because of the work. Get a quote if customers visit you or you work at their site. Ask the insurer for a COI when a contract requires proof.', 'La responsabilidad general comercial paga si alguien se lastima o hay daño a bienes por el trabajo. Pida una cotización si los clientes lo visitan o usted trabaja en su sitio. Pida al asegurador un COI cuando un contrato exija prueba.'],
      hint: ['Quote, policy, or a note that you declined', 'Cotización, póliza o nota de que declinó'],
      priority: 'recommended',
      sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
      sourceUrl: SBA_INSURANCE,
      proof: ['Save the quote or the COI', 'Guarde la cotización o el COI'],
  }));

  if (answers.activity === 'professional' || answers.activity === 'online') {
    stay.push(step(language, {
      id: 'insurance-eo',
      title: ['Decide on E&O insurance', 'Decida el seguro E&O'],
      action: answers.activity === 'online'
        ? ['Get a technology errors-and-omissions quote. A generic professional policy may not cover a software failure. This pays if the service itself is wrong. Cyber insurance does not replace it.', 'Pida una cotización de errores y omisiones de tecnología. Una póliza profesional genérica puede no cubrir una falla del software. Esto paga si el servicio en sí es incorrecto. El seguro cibernético no lo sustituye.']
        : ['Errors and omissions, also called professional liability, pays if the advice or service is wrong. Get a quote if you sell professional or consulting services.', 'Errores y omisiones, también llamado responsabilidad profesional, paga si el consejo o el servicio es incorrecto. Pida una cotización si vende servicios profesionales o de consultoría.'],
      hint: ['Quote or a note that you declined', 'Cotización o nota de que declinó'],
      priority: 'recommended',
      sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
      sourceUrl: SBA_INSURANCE,
      proof: ['Save the quote or the COI', 'Guarde la cotización o el COI'],
    }));
  }

  if (answers.activity === 'online') {
    stay.push(step(language, {
      id: 'insurance-cyber',
      title: ['Decide on cyber insurance', 'Decida el seguro cibernético'],
      action: ['Cyber insurance pays if customer data is exposed or the service is knocked offline. Errors and omissions insurance does not replace it. Ask whether social-engineering and funds-transfer fraud are included. A notice often asks for about $2 million of cyber coverage. Write down the limit the notice names and the limit on the quote. Two million dollars is not a legal minimum. Get a quote before an agency contract asks for it.', 'El seguro cibernético paga si se exponen datos de clientes o el servicio queda fuera de línea. El seguro de errores y omisiones no lo sustituye. Pregunte si incluye ingeniería social y fraude en transferencias. Un aviso a menudo pide unos 2 millones de dólares de cobertura ciber. Anote el límite que nombra el aviso y el límite de la cotización. Dos millones de dólares no son un mínimo legal. Pida una cotización antes de que un contrato de una agencia lo exija.'],
      hint: ['Quote or a note that you declined', 'Cotización o nota de que declinó'],
      priority: 'recommended',
      sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
      sourceUrl: SBA_INSURANCE,
      proof: ['Save the quote or the COI', 'Guarde la cotización o el COI'],
    }));
  }

  if (!sole) {
    stay.push(step(language, {
      id: 'insurance-do',
      title: ['Decide on D&O insurance', 'Decida el seguro D&O'],
      action: ['Directors and officers insurance pays if an owner or manager is sued over how the company is run. Get a quote. You can wait to buy it until a contract or an outside investor requires it.', 'El seguro de directores y oficiales paga si demandan a un dueño o gerente por cómo se dirige la empresa. Pida una cotización. Puede esperar a comprarlo hasta que un contrato o un inversionista externo lo exija.'],
      hint: ['Quote, policy, or a note that you are waiting', 'Cotización, póliza o nota de que está esperando'],
      priority: 'recommended',
      sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
      sourceUrl: SBA_INSURANCE,
      proof: ['Save the quote or the note', 'Guarde la cotización o la nota'],
    }));
  }

  stay.push(step(language, {
    id: 'insurance-epli',
    title: ['Decide on EPLI insurance', 'Decida el seguro EPLI'],
    action: ['Before the first employee, get an employment-practices quote. It pays claims such as wrongful firing, discrimination, harassment, or retaliation. It does not replace workers’ compensation.', 'Antes del primer empleado, pida una cotización de prácticas de empleo. Paga reclamos como despido injusto, discriminación, acoso o represalias. No sustituye la compensación laboral.'],
    hint: ['Quote or a note that you have no employees yet', 'Cotización o nota de que aún no hay empleados'],
    priority: 'recommended',
    sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
    sourceUrl: SBA_INSURANCE,
    proof: ['Save the quote or the note', 'Guarde la cotización o la nota'],
  }));

  stay.push(step(language, {
    id: 'insurance-auto',
    title: ['Decide on business auto insurance', 'Decida el seguro de auto del negocio'],
    action: ['If an owner or contractor drives a personal or rented car for the company, personal auto insurance usually will not cover that trip. Add hired and non-owned auto. Buy a full business auto policy when the company owns a vehicle. Read the contract for the limit it asks for.', 'Si un dueño o contratista maneja un auto personal o alquilado para la empresa, el seguro personal normalmente no cubre ese viaje. Añada auto alquilado y de no propietarios. Compre una póliza comercial completa cuando la empresa tenga un vehículo. Lea en el contrato el límite que pide.'],
    hint: ['Quote, or a note that nobody drives for the company', 'Cotización, o nota de que nadie maneja para la empresa'],
    priority: 'recommended',
    sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
    sourceUrl: SBA_INSURANCE,
    proof: ['Save the quote or the note', 'Guarde la cotización o la nota'],
  }));

  stay.push(step(language, {
    id: 'insurance-property',
    title: ['Decide on business property coverage', 'Decida la cobertura de bienes del negocio'],
    action: ['If the company owns computers or other equipment, a homeowners or renters policy often excludes business property. Ask for business property coverage. A BOP often packages that with general liability. Do not buy a second general liability policy if the package already includes it.', 'Si la empresa tiene computadoras u otro equipo, una póliza de casa o de inquilino a menudo excluye bienes del negocio. Pida cobertura de bienes del negocio. Una BOP suele empaquetar eso con la responsabilidad general. No compre una segunda póliza de responsabilidad general si el paquete ya la incluye.'],
    hint: ['Quote, or a note that the company owns no equipment', 'Cotización, o nota de que la empresa no tiene equipo'],
    priority: 'recommended',
    sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
    sourceUrl: SBA_INSURANCE,
    proof: ['Save the quote or the note', 'Guarde la cotización o la nota'],
  }));

  stay.push(step(language, {
    id: 'insurance-crime',
    title: ['Decide on crime coverage', 'Decida la cobertura de crimen'],
    action: ['Wire fraud and fake-invoice losses are often outside a basic cyber policy. Ask whether social-engineering and funds-transfer fraud are included. Add crime coverage if they are not.', 'El fraude por transferencia y las facturas falsas a menudo quedan fuera de una póliza cibernética básica. Pregunte si incluye ingeniería social y fraude en transferencias. Añada cobertura de crimen si no están.'],
    hint: ['Endorsement, crime quote, or a note that cyber already includes it', 'Endoso, cotización de crimen, o nota de que el seguro cibernético ya lo incluye'],
    priority: 'recommended',
    sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
    sourceUrl: SBA_INSURANCE,
    proof: ['Save the endorsement or the quote', 'Guarde el endoso o la cotización'],
  }));

  stay.push(step(language, {
    id: 'insurance-umbrella',
    title: ['Decide on an umbrella policy', 'Decida una póliza paraguas'],
    action: ['An umbrella adds limits above general liability when a contract asks for more than that policy pays. It does not cover losses the underlying policy excludes. Get the quote after general liability is chosen.', 'Una póliza paraguas añade límites por encima de la responsabilidad general cuando un contrato pide más de lo que esa póliza paga. No cubre pérdidas que la póliza de abajo excluye. Pida la cotización después de elegir la responsabilidad general.'],
    hint: ['Quote, or a note that no contract asks for higher limits', 'Cotización, o nota de que ningún contrato pide límites más altos'],
    priority: 'recommended',
    dependsOn: ['insurance-cgl'],
    sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
    sourceUrl: SBA_INSURANCE,
    proof: ['Save the quote or the note', 'Guarde la cotización o la nota'],
  }));

  stay.push(step(language, {
    id: 'insurance-fiduciary',
    title: ['Decide on fiduciary liability', 'Decida la responsabilidad fiduciaria'],
    action: ['If the company sponsors a retirement plan, add fiduciary liability. Skip the purchase until that plan exists.', 'Si la empresa patrocina un plan de retiro, añada responsabilidad fiduciaria. No la compre hasta que ese plan exista.'],
    hint: ['Quote, or a note that there is no retirement plan', 'Cotización, o nota de que no hay plan de retiro'],
    priority: 'recommended',
    sourceLabel: ['SBA — business insurance', 'SBA — seguro del negocio'],
    sourceUrl: SBA_INSURANCE,
    proof: ['Save the quote or the note', 'Guarde la cotización o la nota'],
  }));

  if (answers.workers !== 'employees') {
    stay.push(step(language, {
      id: 'workers-comp-later',
      title: ['Decide on workers’ compensation before hiring', 'Decida la compensación laboral antes de contratar'],
      action: answers.state === 'TX'
        ? ['Texas does not require workers’ compensation for most private employers. Buy it before the first employee if a contract requires it. Health insurance is not a substitute.', 'Texas no exige compensación laboral a la mayoría de los empleadores privados. Cómprela antes del primer empleado si un contrato la exige. El seguro de salud no la sustituye.']
        : [`Confirm ${stateName}’s workers’ compensation rule before the first employee. Health insurance is not a substitute.`, `Confirme la regla de compensación laboral de ${stateName} antes del primer empleado. El seguro de salud no la sustituye.`],
      hint: ['Quote, waiver, or a note that there are no employees', 'Cotización, exención o nota de que no hay empleados'],
      priority: 'recommended',
      sourceLabel: workersCompHowTo(answers.state).label,
      sourceUrl: workersCompHowTo(answers.state).url,
      proof: ['Save the quote, the waiver, or the note', 'Guarde la cotización, la exención o la nota'],
    }));
  }

  if (!sole) {
    stay.push(step(language, {
      id: 'amendment-log',
      title: ['Log later changes for the state', 'Registre cambios posteriores para el estado'],
      action: ['If the name, address, or management changes, file the state’s amendment. Keep a log of what changed and when you filed it.', 'Si cambian el nombre, la dirección o la administración, presente la enmienda del estado. Lleve un registro de qué cambió y cuándo lo presentó.'],
      hint: ['Amendment log location', 'Dónde está el registro de enmiendas'],
      priority: 'recommended',
      planMin: 'advanced',
      sourceLabel: answers.state === 'TX'
        ? ['Texas Secretary of State — amendment forms', 'Secretario de Estado de Texas — formularios de enmienda']
        : SBA_REGISTER_HOW.label,
      sourceUrl: answers.state === 'TX' ? TX_FORMS : SBA_REGISTER,
      proof: ['Save the log location', 'Guarde la ubicación del registro'],
    }));
    stay.push(step(language, {
      id: 'good-standing',
      title: ['Pull a certificate of good standing before a contract', 'Pida un certificado de vigencia antes de un contrato'],
      action: [`Before a bank, landlord, or customer asks, know how to order a ${stateName} certificate of good standing.`, `Antes de que lo pidan un banco, un arrendador o un cliente, sepa cómo pedir un certificado de vigencia de ${stateName}.`],
      hint: ['How to order the certificate', 'Cómo pedir el certificado'],
      priority: 'recommended',
      planMin: 'advanced',
      sourceLabel: answers.state === 'TX'
        ? ['Texas Secretary of State — order a certificate of status', 'Secretario de Estado de Texas — pida un certificado de vigencia']
        : SBA_REGISTER_HOW.label,
      sourceUrl: answers.state === 'TX' ? TX_CERTIFICATE : SBA_REGISTER,
      proof: ['Save the ordering steps or a recent certificate', 'Guarde los pasos para pedirlo o un certificado reciente'],
    }));
  }

  if (answers.entity === 'corporation' || answers.entity === 'nonprofit') {
    stay.push(step(language, {
      id: 'meeting-minutes',
      title: ['Keep meeting minutes', 'Guarde actas de reuniones'],
      action: ['Write minutes for the organizational meeting and the annual meeting. They are the company’s record of who decided what.', 'Redacte actas de la reunión de organización y de la reunión anual. Son el registro de quién decidió qué.'],
      hint: ['Minutes file location', 'Dónde están las actas'],
      priority: 'recommended',
      planMin: 'advanced',
      sourceLabel: ['SBA — choose a business structure', 'SBA — elija la estructura del negocio'],
      sourceUrl: SBA_STRUCTURE,
      proof: ['Save where the minutes live. Do not paste the whole discussion here.', 'Guarde dónde están las actas. No pegue aquí toda la discusión.'],
    }));
  }

  const government: ComplianceChecklistItemDef[] = [];
  if (!sole && filled(answers.state)) {
    government.push(step(language, {
      id: 'sam-registration',
      title: ['Register on SAM.gov', 'Regístrese en SAM.gov'],
      action: ['After the EIN and the bank account, register the company on SAM.gov if it will bid on federal work or be paid as a prime. SAM.gov assigns the UEI during that registration. Renew it every 365 days. Do not invent a UEI or a CAGE.', 'Después del EIN y la cuenta bancaria, registre la empresa en SAM.gov si va a licitar trabajo federal o a cobrar como contratista principal. SAM.gov asigna el UEI durante ese registro. Renueve cada 365 días. No invente un UEI ni un CAGE.'],
      hint: ['Active SAM.gov registration', 'Registro activo en SAM.gov'],
      priority: 'recommended',
      dependsOn: ['ein', 'business-bank'],
      sourceLabel: ['Register for federal awards', 'Regístrese para adjudicaciones federales'],
      sourceUrl: 'https://sam.gov/entity-registration',
      proof: ['Save the registration status. Do not paste the UEI into notes until SAM.gov shows it.', 'Guarde el estado del registro. No pegue el UEI en las notas hasta que SAM.gov lo muestre.'],
    }));
    government.push(step(language, {
      id: 'gsa-schedule',
      title: ['Decide on a GSA schedule', 'Decida una lista de GSA'],
      action: ['A GSA Multiple Award Schedule is a contract you win after SAM.gov is Active. It is not a signup. Read the current solicitation and answer only when the work matches. Do not say the company holds a Schedule until GSA awards it.', 'Una Lista de Adjudicación Múltiple de GSA es un contrato que se gana cuando SAM.gov está Active. No es un registro. Lea la licitación vigente y responda solo cuando el trabajo coincida. No diga que la empresa tiene una lista hasta que GSA la adjudique.'],
      hint: ['Solicitation you will answer, or a note that you are waiting', 'Licitación que responderá, o una nota de que está esperando'],
      priority: 'recommended',
      dependsOn: ['sam-registration'],
      sourceLabel: ['How federal schedule contracts work', 'Cómo funcionan los contratos federales de lista'],
      sourceUrl: 'https://www.gsa.gov/sell-to-government',
      proof: ['Save the solicitation or the award. Do not call a draft an award.', 'Guarde la licitación o la adjudicación. No llame adjudicación a un borrador.'],
    }));
    government.push(step(language, {
      id: 'local-bids',
      title: ['Register for local bid notices', 'Regístrese para avisos de licitación locales'],
      action: ['Register with the county and the city where this company will look for local work. Each place has its own board. A county supplier portal emails notices for the commodity codes you attach. A city vendor account does the same for that city’s goods and professional services. Some cities only post notices and do not mail them. Skip any board this company will not use. Have a W-9 ready before that government pays you.', 'Regístrese en el condado y la ciudad donde esta empresa buscará trabajo local. Cada lugar tiene su propio tablero. Un portal de proveedores del condado envía avisos para los códigos de mercancía que usted adjunte. Una cuenta de proveedor de la ciudad hace lo mismo para los bienes y servicios profesionales de esa ciudad. Algunas ciudades solo publican avisos y no los envían por correo. Omita cualquier tablero que esta empresa no vaya a usar. Tenga un W-9 listo antes de que ese gobierno le pague.'],
      hint: ['Registrations for the places this company will serve', 'Registros de los lugares que esta empresa va a servir'],
      priority: 'recommended',
      dependsOn: ['formation-filing'],
      sourceLabel: ['SBA — register your business', 'SBA — registre su negocio'],
      sourceUrl: SBA_REGISTER,
      proof: ['Save each registration, or a note naming the boards this company will not use', 'Guarde cada registro, o una nota con los tableros que esta empresa no usará'],
    }));
  }
  if (answers.state === 'TX' && !sole) {
    government.push(step(language, {
      id: 'esbd-watch',
      title: ['Watch the Texas ESBD', 'Vigile el ESBD de Texas'],
      action: ['Search the Electronic State Business Daily for solicitations that match the work. To get those notices by email, register on the CMBL after the EIN. The published CMBL fee is $70 a year. Pick only class codes the company can perform. This is not a DIR contract.', 'Busque en el Boletín Electrónico de Negocios del Estado las licitaciones que coincidan con el trabajo. Para recibir esos avisos por correo, regístrese en el CMBL después del EIN. La tarifa publicada del CMBL es $70 al año. Elija solo códigos de clase que la empresa pueda cumplir. Esto no es un contrato DIR.'],
      hint: ['CMBL receipt or the ESBD search you are watching', 'Recibo del CMBL o la búsqueda del ESBD que está vigilando'],
      priority: 'recommended',
      dependsOn: ['ein'],
      sourceLabel: ['Texas statewide bid board', 'Tablero estatal de licitaciones de Texas'],
      sourceUrl: 'https://www.txsmartbuy.gov/esbd',
      proof: ['Save the CMBL receipt or the bid you are watching', 'Guarde el recibo del CMBL o la licitación que está vigilando'],
    }));
    government.push(step(language, {
      id: 'hub-decision',
      title: ['Decide on Texas HUB', 'Decida el HUB de Texas'],
      action: ['Do not file a HUB application from a race or sex claim. As of May 12, 2026 the Comptroller program is VetHUB: at least 51 percent owned, managed, and run by a Texas resident who is a service-disabled veteran with a disability rating of at least 20 percent. File only when those facts are true. Do not put HUB on a website before the Comptroller certifies the company.', 'No presente una solicitud HUB por raza o sexo. Desde el 12 de mayo de 2026 el programa del Contralor es VetHUB: al menos 51 por ciento de propiedad, gestión y operación por un residente de Texas que es veterano con discapacidad de servicio y una calificación de al menos 20 por ciento. Presente solo cuando esos hechos sean ciertos. No ponga HUB en un sitio antes de que el Contralor certifique a la empresa.'],
      hint: ['Certification, or a note that the facts do not match', 'Certificación, o una nota de que los hechos no coinciden'],
      priority: 'recommended',
      dependsOn: ['governing-document'],
      sourceLabel: ['Texas vendor preference rules', 'Reglas de preferencia de proveedores de Texas'],
      sourceUrl: 'https://comptroller.texas.gov/purchasing/vendor/hub/hub-faq.php',
      proof: ['Save the Comptroller decision or the note that you will not apply', 'Guarde la decisión del Contralor o la nota de que no va a solicitar'],
    }));
  }

  const brandDepends = sole ? ['name-availability'] : ['formation-filing'];
  const brandAndCustomers: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'domain-name',
      title: ['Register the company domain name', 'Registre el dominio de la empresa'],
      action: [`Buy the matching .com for ${answers.legalName} before you print cards or launch ads. Prefer a short name people can say aloud. If the .com is gone, decide whether to change the brand name before you print anything.`, `Compre el .com que coincida con ${answers.legalName} antes de imprimir tarjetas o lanzar anuncios. Prefiera un nombre corto que la gente pueda decir en voz alta. Si el .com no está, decida si cambia la marca antes de imprimir.`],
      hint: ['Registrar receipt for the domain', 'Recibo del registrador del dominio'],
      priority: 'required',
      dependsOn: brandDepends,
      sourceLabel: ['SBA — market your business', 'SBA — comercialice su negocio'],
      sourceUrl: SBA_MARKET,
      proof: ['Save the domain receipt and the renewal date', 'Guarde el recibo del dominio y la fecha de renovación'],
    }),
    ...companyEmailItems(language, 'domain-name'),
    step(language, {
      id: 'company-website',
      title: ['Publish a simple company website', 'Publique un sitio web simple de la empresa'],
      action: ['Put a live page on the domain with what the company does, how to contact you, and HTTPS. Make it readable on a phone. A short page is enough for launch; improve it after real customers reply.', 'Ponga una página en vivo en el dominio con lo que hace la empresa, cómo contactarlo y HTTPS. Que se lea en un teléfono. Una página corta basta para el lanzamiento; mejórela cuando respondan clientes reales.'],
      hint: ['Live URL on the company domain', 'URL en vivo en el dominio de la empresa'],
      priority: 'required',
      dependsOn: ['domain-name'],
      sourceLabel: ['SBA — market your business', 'SBA — comercialice su negocio'],
      sourceUrl: SBA_MARKET,
      proof: ['Save the live URL and a screenshot of the homepage', 'Guarde la URL en vivo y una captura de la página de inicio'],
    }),
    step(language, {
      id: 'google-business-profile',
      title: ['Claim the Google Business Profile', 'Reclame el perfil de Google Business'],
      action: ['If customers find you locally, claim and verify the Google Business Profile. Use the same legal or DBA name, phone, and address you put on the website. Skip this only when the company has no local storefront and no local service area.', 'Si los clientes lo encuentran en el área local, reclame y verifique el perfil de Google Business. Use el mismo nombre legal o DBA, teléfono y dirección que en el sitio. Omítalo solo si la empresa no tiene local ni zona de servicio local.'],
      hint: ['Verified Google Business Profile', 'Perfil de Google Business verificado'],
      priority: 'recommended',
      dependsOn: ['company-website'],
      sourceLabel: ['Google Business Profile', 'Perfil de Google Business'],
      sourceUrl: GOOGLE_BUSINESS_PROFILE,
      proof: ['Save the verified listing URL', 'Guarde la URL del listado verificado'],
    }),
    step(language, {
      id: 'social-handles',
      title: ['Reserve the social media handles', 'Reserve los nombres en redes sociales'],
      action: ['Claim the company name on the networks this company will actually use. Matching handles make cards and the website easier to trust. Do not invent activity on a network you will not check.', 'Reserve el nombre de la empresa en las redes que sí va a usar. Nombres iguales hacen más creíbles las tarjetas y el sitio. No invente actividad en una red que no va a revisar.'],
      hint: ['List of reserved handles', 'Lista de nombres reservados'],
      priority: 'recommended',
      dependsOn: brandDepends,
      sourceLabel: ['SBA — market your business', 'SBA — comercialice su negocio'],
      sourceUrl: SBA_MARKET,
      proof: ['Save the handle list and who can log in', 'Guarde la lista de nombres y quién puede entrar'],
    }),
    step(language, {
      id: 'logo-and-cards',
      title: ['Make a logo and business cards', 'Haga un logo y tarjetas de presentación'],
      action: ['Create a simple logo and print cards with the legal or DBA name, phone, email on the company domain, and website. LLC or Inc. on the card is optional; keep the legal name on filings and bank forms. Do not print until the domain and email exist.', 'Cree un logo simple e imprima tarjetas con el nombre legal o DBA, teléfono, correo en el dominio de la empresa y sitio web. LLC o Inc. en la tarjeta es opcional; conserve el nombre legal en las presentaciones y el banco. No imprima hasta que existan el dominio y el correo.'],
      hint: ['Logo file and card proof', 'Archivo del logo y prueba de las tarjetas'],
      priority: 'recommended',
      dependsOn: ['business-email-check', 'company-website'],
      sourceLabel: ['SBA — market your business', 'SBA — comercialice su negocio'],
      sourceUrl: SBA_MARKET,
      proof: ['Save the logo file and a photo or PDF of the card', 'Guarde el archivo del logo y una foto o PDF de la tarjeta'],
    }),
    step(language, {
      id: 'marketing-launch',
      title: ['Write the first marketing plan and outreach list', 'Escriba el primer plan de marketing y la lista de contactos'],
      action: ['Write one page: who the first customers are, the one sentence that describes the offer, where you will announce the launch, and a list of 10 to 20 people or companies to contact in the first two weeks. Track replies. Website Compliance still watches the public site separately.', 'Escriba una página: quiénes son los primeros clientes, la frase que describe la oferta, dónde anunciará el lanzamiento y una lista de 10 a 20 personas o empresas para contactar en las primeras dos semanas. Lleve el seguimiento de las respuestas. Cumplimiento del sitio web sigue vigilando el sitio público por separado.'],
      hint: ['One-page plan plus outreach list', 'Plan de una página y lista de contactos'],
      priority: 'recommended',
      dependsOn: ['company-website', 'business-email-check'],
      sourceLabel: ['SBA — market your business', 'SBA — comercialice su negocio'],
      sourceUrl: SBA_MARKET,
      proof: ['Save the plan and the dated outreach list', 'Guarde el plan y la lista de contactos con fecha'],
    }),
  ];

  if (answers.offers === 'product') {
    brandAndCustomers.unshift(
      step(language, {
        id: 'name-the-product',
        title: ['Name the product this company sells', 'Nombre el producto que vende esta empresa'],
        action: ['Write the product or subscription name, such as the software customers pay for. That name is a product, not a second legal entity.', 'Escriba el nombre del producto o de la suscripción, como el software que pagan los clientes. Ese nombre es un producto, no una segunda entidad legal.'],
        hint: ['Product name note', 'Nota con el nombre del producto'],
        priority: 'blocking',
        dependsOn: ['company-website'],
        proof: ['Save the product name in a note', 'Guarde el nombre del producto en una nota'],
      }),
      step(language, {
        id: 'one-sanctions-program',
        title: ['Keep one sanctions program for this company', 'Mantenga un solo programa de sanciones para esta empresa'],
        action: ['Screen customers of the product under this company’s sanctions program. Do not open a second program just because the product has its own name.', 'Criba a los clientes del producto bajo el programa de sanciones de esta empresa. No abra un segundo programa solo porque el producto tenga su propio nombre.'],
        hint: ['Note that customer screening stays on this company', 'Nota de que el cribado de clientes queda en esta empresa'],
        priority: 'blocking',
        dependsOn: ['name-the-product'],
        proof: ['Save the note', 'Guarde la nota'],
      }),
    );
  }
  if (answers.offers === 'subsidiary') {
    formation.push(step(language, {
      id: 'owned-company-record',
      title: ['Open a formation record for the company this one owns', 'Abra un expediente de formación para la empresa de la que esta es dueña'],
      action: ['A subsidiary is a separate company. It needs its own state filing, tax number, and bank account. Do not treat this company’s filing as the owned company’s filing.', 'Una subsidiaria es otra empresa. Necesita su propia presentación estatal, número fiscal y cuenta bancaria. No trate la presentación de esta empresa como la de la empresa que posee.'],
      hint: ['Name of the owned company', 'Nombre de la empresa que posee'],
      priority: 'blocking',
      proof: ['Save the owned company’s legal name', 'Guarde la razón social de la empresa que posee'],
    }));
  }

  const pillars: CompliancePillarDef[] = [
    {
      id: 'formation',
      label: tx(language, 'Form the company', 'Formar la empresa'),
      summary: tx(language, 'Name, state filing, registered agent, and the governing document.', 'Nombre, presentación estatal, agente registrado y documento de gobierno.'),
      items: formation,
    },
    {
      id: 'tax-and-banking',
      label: tx(language, 'Tax ID and bank', 'ID fiscal y banco'),
      summary: tx(language, 'EIN, any tax election, the business bank account, and beneficial-ownership status.', 'EIN, cualquier elección fiscal, la cuenta bancaria y el estado de titularidad real.'),
      items: taxItems,
    },
    {
      id: 'licenses',
      label: tx(language, 'Licenses and permits', 'Licencias y permisos'),
      summary: tx(language, 'Activity licenses and sales tax, based on what the business does.', 'Licencias de la actividad e impuesto sobre ventas, según lo que hace el negocio.'),
      items: licenseItems,
    },
    {
      id: 'brand-and-customers',
      label: tx(language, 'Brand and customers', 'Marca y clientes'),
      summary: tx(
        language,
        'Domain, company email, website, local listing, social handles, cards, and the first outreach plan.',
        'Dominio, correo de la empresa, sitio web, listado local, redes, tarjetas y el primer plan de contacto.',
      ),
      items: brandAndCustomers,
    },
  ];
  if (government.length) {
    pillars.push({
      id: 'government-buyers',
      label: tx(language, 'Government buyers', 'Compradores del gobierno'),
      summary: tx(language, 'Federal registration, state bid notices where they apply, and the county and city boards where this company will look for work.', 'Registro federal, avisos estatales cuando aplican, y los tableros del condado y la ciudad donde esta empresa buscará trabajo.'),
      items: government,
    });
  }
  if (people.length) {
    pillars.push({
      id: 'people',
      label: tx(language, 'People you pay', 'Personas a las que paga'),
      summary: tx(language, 'Payroll, workers’ compensation, or contractor W-9s.', 'Nómina, compensación laboral o W-9 de contratistas.'),
      items: people,
    });
  }
  if (stay.length) {
    pillars.push({
      id: 'stay-current',
      label: tx(language, 'Stay in good standing', 'Mantenerse al día'),
      summary: tx(language, 'Other states, insurance, amendments, and recurring records.', 'Otros estados, seguros, enmiendas y registros periódicos.'),
      items: stay,
    });
  }
  return pillars;
};

const answerScore = (answers: BusinessCreationAnswers | undefined): number => {
  if (!answers) {
    return 0;
  }
  return Object.entries(answers).filter(([key, value]) => (
    key !== 'legalName' && value !== undefined && value !== null && String(value).trim() !== ''
  )).length;
};

const namesMatchCompany = (candidate: string, legalName: string): boolean => {
  const left = candidate.trim().toLowerCase();
  const right = legalName.trim().toLowerCase();
  if (!left || !right) {
    return false;
  }
  return left === right || left === `${right} llc` || right === `${left} llc`;
};

/** Keep the interview on the company named in Which company. A name-only card must not hide answers already saved. */
export const alignBusinessCreationToCompany = (
  state: ComplianceConsoleState,
): ComplianceConsoleState => {
  const taxonomy = state.taxonomy;
  const company = taxonomy?.companies.find((row) => row.id === taxonomy.activeCompanyId);
  const name = company?.name.trim() || '';
  if (!name || !company) {
    return state;
  }
  const own = state.companyChecks?.[company.id];
  const snapshots = [
    own,
    ...Object.values(state.companyChecks || {}).filter((row) => row !== own),
  ].filter((row) => {
    if (!row) {
      return false;
    }
    if (row === own) {
      return true;
    }
    const savedName = String(row.businessCreation?.legalName || '').trim();
    return !savedName || namesMatchCompany(savedName, name);
  });
  const ranked = [state.businessCreation, ...snapshots.map((row) => row.businessCreation)]
    .filter((row): row is BusinessCreationAnswers => Boolean(row));
  const best = ranked.reduce<BusinessCreationAnswers | undefined>((winner, row) => (
    answerScore(row) > answerScore(winner) ? row : winner
  ), undefined);
  const bestItems = snapshots.reduce<ComplianceConsoleState['items'] | undefined>((winner, row) => {
    const count = Object.keys(row?.items || {}).length;
    return count > Object.keys(winner || {}).length ? row.items : winner;
  }, undefined);
  const nextAnswers: BusinessCreationAnswers = {
    ...(best || {}),
    legalName: name,
  };
  const rootScore = answerScore(state.businessCreation);
  const nextScore = answerScore(nextAnswers);
  const rootItems = Object.keys(state.items || {}).length;
  const nextItems = Object.keys(bestItems || {}).length;
  if (state.businessCreation?.legalName === name && nextScore <= rootScore && nextItems <= rootItems) {
    return state;
  }
  return {
    ...state,
    updatedAt: new Date().toISOString(),
    targetCompanyName: name,
    businessCreation: nextAnswers,
    items: nextItems > rootItems && bestItems ? bestItems : state.items,
    companyChecks: {
      ...(state.companyChecks || {}),
      [company.id]: {
        ...(state.companyChecks?.[company.id] || {}),
        businessCreation: nextAnswers,
        items: nextItems > rootItems && bestItems ? bestItems : (state.companyChecks?.[company.id]?.items || state.items),
      },
    },
  };
};

export const ensureBusinessCompany = (
  state: ComplianceConsoleState,
  answers: BusinessCreationAnswers,
): ComplianceConsoleState => {
  const name = String(answers.legalName || '').trim();
  if (!name) {
    return { ...state, businessCreation: answers };
  }
  const taxonomy = state.taxonomy || emptyDodTaxonomy();
  const existing = taxonomy.companies.find((row) => row.name.trim().toLowerCase() === name.toLowerCase());
  if (existing) {
    return selectDodCompany({
      ...state,
      targetCompanyName: name,
      businessCreation: answers,
    }, existing.id);
  }
  const id = newDodId('company');
  return selectDodCompany({
    ...state,
    targetCompanyName: name,
    businessCreation: answers,
    taxonomy: {
      ...taxonomy,
      companies: [...taxonomy.companies, { id, name, ein: '' }],
    },
  }, id);
};
